import React from "react";
import { sortAlpha } from "../lib/sortUtils";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { EquineRationBagIcon, HorseIcon } from "../lib/icons";
import { buildNextRecordNumber } from "../lib/recordNumberUtils";
import { appendProvisionalSuffix, appendUpdatePendingDisplaySuffix, markOperationalCreatePending, markOperationalUpdatePending } from "../lib/operationalProvisional";
import { getNativeSafeAsset } from "../lib/platformAssets";

const harasHayCardIcon = getNativeSafeAsset("/icons/shared/fardo-de-feno-card.webp");
const equineRationCardIcon = getNativeSafeAsset("/icons/equinos/saco-racao-equinos-card.webp");
const cardImageStyle = { width: 34, height: 34, objectFit: "contain", display: "block" };
function renderCardImageIcon(src, alt){
  return <img src={src} alt={alt} loading="eager" decoding="async" fetchpriority="high" style={cardImageStyle} />;
}

export function createHarasModule(deps){
  const {
    useState,
    useEffect,
    useSysLists,
    useFarm,
    useFarmCollection,
    useGlobalCollection,
    useToast,
    useBgSyncToast,
    useReliableSyncOnSave,
    canViewCostCenter,
    canEditCostCenter,
    getDeniedCostCentersForModule,
    T,
    CC_COLORS,
    DEFAULT_SKIP_REASONS,
    createClientId,
    makeCreateMeta,
    makeUpdateMeta,
    DB,
    pushCollection,
    isCompactViewport,
    fmtDate,
    Btn,
    Modal,
    Field,
    Input,
    Select,
    Textarea,
    StatCard,
    Toast,
    Badge,
  } = deps;

const HARAS_BREEDS = [
  "Mangalarga Marchador","Quarto de Milha","Campolina","Lusitano","Puro Sangue Inglês (PSI)",
  "Árabe","Paint Horse","Appaloosa","Andaluz","Frísio","Crioulo","Pantaneiro","Outro"
];

const DEFAULT_COAT_COLORS = [
  {name:"Alazão",         hex:"#C1440E"},
  {name:"Castanho",       hex:"#6B3A2A"},
  {name:"Zaino",          hex:"#1A0A00"},
  {name:"Tordilho",       hex:"#A0A0A0"},
  {name:"Ruão",           hex:"#C97B47"},
  {name:"Palomino",       hex:"#D4A853"},
  {name:"Bai",            hex:"#8B5E3C"},
  {name:"Overo",          hex:"#F5F0E8"},
  {name:"Tobiano",        hex:"#E8D5B0"},
  {name:"Appaloosa",      hex:"#D4C5A9"},
  {name:"Cremelo",        hex:"#F5ECD7"},
  {name:"Perlino",        hex:"#EDD9B0"},
  {name:"Buckskin",       hex:"#C8A86B"},
  {name:"Grulla",         hex:"#8B8680"},
  {name:"Outra",          hex:"#64748B"},
];

const WEEK_DAYS = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
const WEEK_DAYS_MONDAY = [
  { index:1, label:"Seg" },
  { index:2, label:"Ter" },
  { index:3, label:"Qua" },
  { index:4, label:"Qui" },
  { index:5, label:"Sex" },
  { index:6, label:"Sáb" },
  { index:0, label:"Dom" },
];

const TRAINING_TYPES = [
  "Adestramento","Salto","Trabalho em Campo","Marcha","Piquete","Trabalho Livre",
  "Banho / Higiene","Ferragem / Casqueamento","Veterinário","Descanso","Outro"
];

const HARAS_FEED_TIMES = ["06:00","11:00","16:00"];
const HARAS_HAY_TIMES = ["07:00","12:00","18:00"];
const HARAS_MANAGEMENT_TYPES = ["Limpeza de Baia","Casqueamento","Ferrageamento","Banho / Higiene","Troca de Cama","Manejo Sanitário","Outro"];
const HARAS_HAY_QUALITY_FACTOR = {"Muito boa":1,"Boa":0.94,"Regular":0.88,"Ruim":0.8};

function getVetStyleStatGridCols(count){
  if(count===3) return "repeat(3,minmax(0,1fr))";
  if(count===4) return "repeat(2,minmax(0,1fr))";
  if(count===5) return "repeat(6,minmax(0,1fr))";
  if(count>=6) return "repeat(3,minmax(0,1fr))";
  return `repeat(${Math.max(1,count)},minmax(0,1fr))`;
}
function getVetStyleStatItemStyle(count,index){
  if(count===5) return {gridColumn:`span ${index<3?2:3}`};
  return undefined;
}

const DIET_ITEMS = [
  "Feno","Capim","Ração","Farelo de Soja","Milho","Aveia","Suplemento Vitamínico",
  "Sal Mineral","Cenoura","Maçã","Outro"
];

// Baia status configs
const BAIA_STATUS = {
  "Limpa":        { color:"#22C55E", icon:"✅" },
  "Suja":         { color:"#EF4444", icon:"🔴" },
  "Não Conforme": { color:"#F59E0B", icon:"⚠️" },
};
const WATER_STATUS  = ["Limpa","Suja","Vazia","Sem Avaliação"];
const TROUGH_STATUS = ["Em Conformidade","Danificado","Sujo","Sem Avaliação"];
const BED_THICKNESS = ["Em Conformidade","Fina Demais","Grossa","Média"];
const BED_MOISTURE  = ["Seca","Úmida","Molhada"];
const REPAIR_STATUS = { "Pendente":"#F59E0B","Em Análise":"#3B82F6","Aprovado":"#22C55E","Negado":"#EF4444" };


function dedupeById(items = []) {
  const map = new Map();
  for (const raw of Array.isArray(items) ? items : []) {
    const item = raw && typeof raw === "object" ? raw : null;
    if (!item) continue;
    const key = String(item.id || "");
    const prev = map.get(key);
    if (!prev) { map.set(key, item); continue; }
    const prevAt = String(prev.updatedAt || prev.createdAt || "");
    const nextAt = String(item.updatedAt || item.createdAt || "");
    if (nextAt >= prevAt) map.set(key, item);
  }
  return Array.from(map.values());
}

function num(v, fallback=0){ const n=parseFloat(v); return Number.isFinite(n)?n:fallback; }
function round(v, digits=2){ const n=Number(v||0); return Number.isFinite(n)? Number(n.toFixed(digits)) : 0; }
function getAgeYears(birthDate){
  if(!birthDate) return null;
  const dt = new Date(birthDate);
  if(Number.isNaN(dt.getTime())) return null;
  const diff = Date.now() - dt.getTime();
  return diff > 0 ? diff / (365.25*24*60*60*1000) : 0;
}
function parseRuleList(list=[]){
  return (Array.isArray(list)?list:[]).map(rule=>({
    ...rule,
    minSessions:num(rule?.minSessions,0),
    maxSessions:num(rule?.maxSessions,999),
    suggestedPctBW:num(rule?.suggestedPctBW,0),
  }));
}
function inferEquineActivityLevel(schedule=[], equineId, activityRules=[]){
  const total = (Array.isArray(schedule)?schedule:[]).filter(item=>String(item?.equineId||'')===String(equineId)).length;
  const parsed = parseRuleList(activityRules);
  const match = parsed.find(rule=> total >= rule.minSessions && total <= rule.maxSessions) || parsed[parsed.length-1] || null;
  return { totalSessions: total, levelName: match?.name || 'Pouco ativo', suggestedPctBW: match?.suggestedPctBW || 0.5, notes: match?.notes || '' };
}
function getBreedRule(breedRules=[], breed=''){
  return (Array.isArray(breedRules)?breedRules:[]).find(rule=>String(rule?.breed||'').toLowerCase()===String(breed||'').toLowerCase()) || null;
}
function suggestNutritionByEquine(eq, activityInfo, breedRule){
  const age = getAgeYears(eq?.birthDate);
  let base = num(activityInfo?.suggestedPctBW, 0.5);
  if(age!==null && age < 3) base += 0.20;
  if(String(eq?.sex||'').toLowerCase()==='fêmea') base += 0.00;
  base += num(breedRule?.pctAdjust, 0);
  const minPct = Math.max(0.15, round(base - 0.15, 2));
  const maxPct = round(base + 0.20, 2);
  return { suggestedPctBW: round(base,2), minPctBW: minPct, maxPctBW: maxPct };
}
function suggestHayByEquine(eq, activityInfo, breedRule, hayQuality='Boa'){
  const age = getAgeYears(eq?.birthDate);
  let base = 1.5;
  const level = String(activityInfo?.levelName||'').toLowerCase();
  if(level.includes('muito pouco') || level.includes('manutenção')) base = 1.4;
  else if(level.includes('pouco')) base = 1.5;
  else if(level.includes('muito ativo')) base = 1.65;
  else if(level.includes('ativo')) base = 1.6;
  else if(level.includes('intenso')) base = 1.75;
  if(age!==null && age < 3) base += 0.1;
  base += Math.max(-0.08, Math.min(0.08, num(breedRule?.pctAdjust, 0) * 0.4));
  const factor = HARAS_HAY_QUALITY_FACTOR[hayQuality] ?? HARAS_HAY_QUALITY_FACTOR['Boa'];
  const suggestedPctBW = round(base / Math.max(factor,0.6), 2);
  const minPctBW = Math.max(1.25, round(suggestedPctBW - 0.15, 2));
  const maxPctBW = round(suggestedPctBW + 0.25, 2);
  return { suggestedPctBW, minPctBW, maxPctBW, qualityFactor: factor };
}
function buildWeightSeries(weights=[], nutritionByEquine, equineId){
  return (Array.isArray(weights)?weights:[])
    .filter(w=>String(w?.equineId||'')===String(equineId))
    .sort((a,b)=>String(a?.date||a?.createdAt||'').localeCompare(String(b?.date||b?.createdAt||''),'pt-BR'))
    .map(item=>({
      ...item,
      weightKg:num(item.weightKg, 0),
      feedKgDay:num(item.feedKgDay, 0),
      forageKgDay:num(item.forageKgDay, 0),
      supplementKgDay:num(item.supplementKgDay, 0),
    }));
}
function MiniTrendChart({ points=[] }){
  const safe = (Array.isArray(points)?points:[]).filter(p=>Number.isFinite(p?.weightKg));
  if(safe.length < 2) return <div style={{fontSize:12,color:T.muted}}>Adicione pelo menos 2 pesagens para visualizar o gráfico.</div>;
  const width=320, height=120, pad=16;
  const maxY=Math.max(...safe.map(p=>p.weightKg), 1);
  const minY=Math.min(...safe.map(p=>p.weightKg), maxY);
  const span=Math.max(maxY-minY, 1);
  const coords=safe.map((p,idx)=>{
    const x = pad + ((width - pad*2) * idx / Math.max(safe.length-1,1));
    const y = height - pad - (((p.weightKg - minY)/span) * (height - pad*2));
    return `${x},${y}`;
  }).join(' ');
  return <svg viewBox={`0 0 ${width} ${height}`} style={{width:'100%',height:120,display:'block'}}>
    <polyline fill="none" stroke={CC_COLORS['Haras']} strokeWidth="3" points={coords} />
    {safe.map((p,idx)=>{ const x = pad + ((width - pad*2) * idx / Math.max(safe.length-1,1)); const y = height - pad - (((p.weightKg - minY)/span) * (height - pad*2)); return <circle key={idx} cx={x} cy={y} r="4" fill={T.yellow} />; })}
  </svg>;
}

function formatStatValue(value, unit){
  return <span style={{display:"inline-flex",alignItems:"baseline",gap:6,flexWrap:"wrap",fontFamily:"'DM Sans',sans-serif"}}><span>{value}</span>{unit?<span style={{fontSize:12,fontWeight:600,color:T.muted,letterSpacing:"0"}}>{unit}</span>:null}</span>;
}

function HarasModule({ session, moduleCardsConfig: moduleCardsConfigProp }) {
  const [equines,      setEquines]      = useFarmCollection("harasEquines",   []);
  const [stalls,       setStalls]       = useFarmCollection("harasStalls",    []);
  const ensureHarasEquineRecordNumbers = React.useCallback((records=[])=>{
    const working = Array.isArray(records) ? records.map(item=> item && typeof item === "object" ? { ...item } : item).filter(Boolean) : [];
    working.forEach((record)=>{ if (!record || record.recordNumber) return; Object.assign(record, buildNextRecordNumber(working.filter(Boolean), { moduleKey:"haras" })); });
    return working;
  }, []);
  const ensureHarasStallRecordNumbers = React.useCallback((records=[])=>{
    const working = Array.isArray(records) ? records.map(item=> item && typeof item === "object" ? { ...item } : item).filter(Boolean) : [];
    working.forEach((record)=>{ if (!record || record.recordNumber) return; Object.assign(record, buildNextRecordNumber(working.filter(Boolean), { moduleKey:"haras" })); });
    return working;
  }, []);
  const [schedule,     setSchedule]     = useFarmCollection("harasSchedule",  []);
  const [nutrition,    setNutrition]    = useFarmCollection("harasNutrition", []);
  const [weights,      setWeights]      = useFarmCollection("harasWeights",   []);
  const [services,     setServices]     = useFarmCollection("services",       []);
  const [,            setPois]         = useFarmCollection("poi",            []);
  const [pastures]                      = useFarmCollection("pastures",       []);
  const [stockItems]                    = useGlobalCollection("stock", []);
  const [people]                        = useGlobalCollection("people", []);
  const SL = useSysLists();
  const deniedCostCenters = React.useMemo(()=>getDeniedCostCentersForModule(session, "haras"), [session]);
  const canViewHaras = canViewCostCenter(session, "haras", "Haras");
  const canEditHaras = canEditCostCenter(session, "haras", "Haras");
  const activeBreeds = React.useMemo(() => (SL.HARAS_BREEDS || HARAS_BREEDS), [SL.HARAS_BREEDS]);
  const activeCoats  = React.useMemo(() => (SL.HARAS_COATS || DEFAULT_COAT_COLORS).map(c=>({ name:String(c?.name||c?.label||""), hex:String(c?.hex||"#64748B") })), [SL.HARAS_COATS]);
  const activityRules = React.useMemo(() => (SL.HARAS_ACTIVITY_RULES || []), [SL.HARAS_ACTIVITY_RULES]);
  const breedRules = React.useMemo(() => (SL.HARAS_BREED_RULES || []), [SL.HARAS_BREED_RULES]);
  const [tab,        setTab]      = useState("plantel");
  const [cardsSection,setCardsSection] = useState("moduleHome");
  const [agendaTab,  setAgendaTab] = useState("treinos");
  const [form,       setForm]     = useState(null);
  const [stallForm,  setStallForm]= useState(null);
  const [detailStall,setDetailStall]=useState(null);
  const [schedForm,  setSchedForm]= useState(null);
  const [nutritionForm,setNutritionForm] = useState(null);
  const [feedTimesForm,setFeedTimesForm] = useState(null);
  const [managementForm,setManagementForm] = useState(null);
  const [guaranteeModal,setGuaranteeModal] = useState(null);
  const [equineDetail, setEquineDetail] = useState(null);
  const [detailSection, setDetailSection] = useState("");
  const [weightForm, setWeightForm] = useState(null);
  const nutritionFormRef = React.useRef(null);
  const feedTimesFormRef = React.useRef(null);
  const managementFormRef = React.useRef(null);
  const scheduleFormRef = React.useRef(null);
  const weightFormRef = React.useRef(null);
  const stallFormRef = React.useRef(null);
  const detailStallRef = React.useRef(null);
  const equineFocusedId = String((form && !form._new ? form.id : "") || equineDetail?.id || weightForm?.equineId || "");
  const isEquineInlineFocusMode = !!(form || equineDetail || weightForm);
  const nutritionFocusedEquineId = String(nutritionForm?.equineId || "");
  const isNutritionInlineFocusMode = !!nutritionForm;
  const [toast,      show]        = useToast();
  const ownerPeople = React.useMemo(()=> (Array.isArray(people) ? people : []).filter(person=>person && person.active !== false && person.roles?.owner).map(person=>({ id:String(person.id||person.clientId||""), name:String(person.name || person.tradeName || "").trim(), tradeName:String(person.tradeName || "").trim() })).filter(person=>person.name), [people]);
  useBgSyncToast("harasEquines", show);
  const syncOnSave = useReliableSyncOnSave();
  const { fid: harasFarmId }      = useFarm();
  const canEdit = (!session || session.role==="admin" || session.role==="gestor") || canEditHaras;
  React.useEffect(()=>{ if(form && typeof window!=="undefined") requestAnimationFrame(()=>nutritionFormRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [form]);
  React.useEffect(()=>{ if(nutritionForm && typeof window!=="undefined") requestAnimationFrame(()=>nutritionFormRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [nutritionForm]);
  React.useEffect(()=>{ if(feedTimesForm && typeof window!=="undefined") requestAnimationFrame(()=>feedTimesFormRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [feedTimesForm]);
  React.useEffect(()=>{ if(managementForm && typeof window!=="undefined") requestAnimationFrame(()=>managementFormRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [managementForm]);
  React.useEffect(()=>{ if(schedForm && typeof window!=="undefined") requestAnimationFrame(()=>scheduleFormRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [schedForm]);
  React.useEffect(()=>{ if(weightForm && typeof window!=="undefined") requestAnimationFrame(()=>weightFormRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [weightForm]);
  React.useEffect(()=>{ if(stallForm && typeof window!=="undefined") requestAnimationFrame(()=>stallFormRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [stallForm]);
  React.useEffect(()=>{ if(detailStall && typeof window!=="undefined") requestAnimationFrame(()=>detailStallRef.current?.scrollIntoView({behavior:"smooth",block:"start"})); }, [detailStall]);
  const isCollab = session?.role==="solicitante";
  const myName   = session?.name||"";
  const [moduleCardsConfigLocal, setModuleCardsConfigLocal] = useState(()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  });
  const [adminGlobalsConfigRows] = useGlobalCollection("adminGlobalsConfig", [{ id:"admin_globals_config", clientId:"admin_globals_config", moduleCardsConfig:{} }]);
  useEffect(()=>{
    function reloadModuleCardsConfig(){
      try{
        const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
        setModuleCardsConfigLocal(parsed && typeof parsed === "object" ? parsed : {});
      }catch(_){ setModuleCardsConfigLocal({}); }
    }
    reloadModuleCardsConfig();
    const onStorage = (e)=>{ if(!e || e.key === "fz:moduleCardsConfig") reloadModuleCardsConfig(); };
    const onUpdated = ()=>reloadModuleCardsConfig();
    window.addEventListener("storage", onStorage);
    window.addEventListener("fz:sync:updated", onUpdated);
    return ()=>{
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("fz:sync:updated", onUpdated);
    };
  }, []);
  const effectiveModuleCardsConfig = React.useMemo(()=>{
    const globalConfig = adminGlobalsConfigRows?.[0]?.moduleCardsConfig;
    if(moduleCardsConfigProp && typeof moduleCardsConfigProp === "object" && Object.keys(moduleCardsConfigProp).length) return moduleCardsConfigProp;
    if(globalConfig && typeof globalConfig === "object" && Object.keys(globalConfig).length) return globalConfig;
    return moduleCardsConfigLocal && typeof moduleCardsConfigLocal === "object" ? moduleCardsConfigLocal : {};
  }, [adminGlobalsConfigRows, moduleCardsConfigLocal, moduleCardsConfigProp]);
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = effectiveModuleCardsConfig?.haras;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [effectiveModuleCardsConfig]);
  const rationItems = React.useMemo(()=> (stockItems||[]).filter(item=>String(item?.category||"")==="Ração").sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""),"pt-BR")), [stockItems]);
  const supplementItems = React.useMemo(()=> (stockItems||[]).filter(item=>String(item?.category||"")==="Suplementação").sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""),"pt-BR")), [stockItems]);
  const hayItems = React.useMemo(()=> (stockItems||[]).filter(item=>String(item?.category||"").toLowerCase()==="feno").sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""),"pt-BR")), [stockItems]);
  const nutritionByEquine = React.useMemo(()=>{
    const map = new Map();
    (nutrition||[]).forEach(entry=>{ if(entry?.equineId) map.set(String(entry.equineId), entry); });
    return map;
  }, [nutrition]);
  const managementServices = React.useMemo(()=> (services||[]).filter(s=>String(s?.harasAgendaType||"")==="manejo").sort((a,b)=>String(a?.plannedDate||a?.date||"").localeCompare(String(b?.plannedDate||b?.date||""),"pt-BR") || String((a?.plannedTimes||[])[0]||"").localeCompare(String((b?.plannedTimes||[])[0]||""),"pt-BR")), [services]);
  const nextBaiaSuffix = React.useMemo(()=>{
    const used = new Set((stalls||[]).map(st=>String(st?.suffix||st?.name||"").match(/(\d{1,})$/)?.[1]||"").filter(Boolean));
    let n = 1;
    while(used.has(String(n).padStart(2,"0"))) n += 1;
    return String(n).padStart(2,"0");
  }, [stalls]);
  function makeHarasCreatePending(record, records = [], prefix = "haras") {
    const baseNumberParts = record?.recordNumber
      ? { recordNumber: record.recordNumber, recordPrefix: record.recordPrefix, recordSuffix: record.recordSuffix, recordSequence: record.recordSequence }
      : buildNextRecordNumber(Array.isArray(records) ? records.filter(Boolean) : [], { moduleKey:"haras" });
    const rn = appendProvisionalSuffix(baseNumberParts.recordNumber || record.recordNumber || "");
    return markOperationalCreatePending({
      ...record,
      ...baseNumberParts,
      recordNumber: rn,
      provisionalRecordNumber: rn,
      recordNumberStatus: "provisional",
      provisional: true,
      clientId: record.clientId || record.id || createClientId(prefix),
    }, { userName: myName || "" });
  }

  function makeHarasUpdatePending(record, operationType = "update") {
    return markOperationalUpdatePending(record, { userName: myName || "", operationType });
  }

  function displayHarasRecordNumber(record) {
    if(!record) return "—";
    const rn = String(record.recordNumber || record.id || "").trim();
    if(!rn) return "—";
    if(record.pendingLocalUpdate && !record.pendingLocalCreate) return appendUpdatePendingDisplaySuffix(rn);
    return rn;
  }

  function ensureHarasSubEntry(entry, prefix) {
    const id = entry?.id || entry?.clientId || createClientId(prefix);
    return { ...entry, id, clientId: entry?.clientId || id };
  }

  const selectedNutritionItems = React.useMemo(()=> (nutrition||[]).map(item=>({
    ...item,
    feedItem: rationItems.find(r=>String(r.id)===String(item.feedStockId)),
    supplementItem: supplementItems.find(r=>String(r.id)===String(item.supplementStockId)),
    hayItem: hayItems.find(r=>String(r.id)===String(item.hayStockId)),
    equine: equines.find(eq=>String(eq.id)===String(item.equineId)),
  })), [nutrition, rationItems, supplementItems, hayItems, equines]);
  const sortedEquines = React.useMemo(()=> (equines||[]).filter(item=>!item?._deleted).slice().sort((a,b)=>String(b?.updatedAt||b?.createdAt||"").localeCompare(String(a?.updatedAt||a?.createdAt||""),"pt-BR") || String(a?.name||"").localeCompare(String(b?.name||""),"pt-BR")), [equines]);
  const sortedStalls = React.useMemo(()=> (stalls||[]).filter(item=>!item?._deleted).slice().sort((a,b)=>String(b?.updatedAt||b?.createdAt||"").localeCompare(String(a?.updatedAt||a?.createdAt||""),"pt-BR") || String(a?.name||"").localeCompare(String(b?.name||""),"pt-BR")), [stalls]);
  const activeEquines = React.useMemo(()=>sortedEquines.filter(e=>e.status==="Ativo"), [sortedEquines]);
  const nutritionStats = React.useMemo(()=>{
    const stockSacks = rationItems.reduce((sum,item)=> sum + ((parseFloat(item.qty)||0) / Math.max(parseFloat(item.defaultQty || item.defaultPackQty || 50) || 50, 0.001)), 0);
    const stockHayBales = (stockItems||[]).filter(item=> String(item?.category||"").toLowerCase()==="feno").reduce((sum,item)=>{
      const qty = parseFloat(item?.qty)||0;
      const baleSize = Math.max(parseFloat(item?.defaultQty || item?.defaultPackQty || 1) || 1, 0.001);
      return sum + (qty / baleSize);
    }, 0);
    const weeklySacks = selectedNutritionItems.reduce((sum,item)=> sum + ((parseFloat(item.feedQty)||0) * 7 / Math.max(parseFloat(item.feedItem?.defaultQty || item.feedItem?.defaultPackQty || 50) || 50, 0.001)), 0);
    const weeklyHayBales = selectedNutritionItems.reduce((sum,item)=> sum + ((parseFloat(item.forageQty)||0) * 7 / Math.max(parseFloat(item.hayItem?.baleWeightKg || item.hayItem?.pkgQty || item.foragePackQty || 12) || 12, 0.001)), 0);
    const totalFeedKgDay = selectedNutritionItems.reduce((sum,item)=> sum + (parseFloat(item.feedQty)||0), 0);
    const totalHayKgDay = selectedNutritionItems.reduce((sum,item)=> sum + (parseFloat(item.forageQty)||0), 0);
    const avgFeedKgEq = activeEquines.length ? totalFeedKgDay / activeEquines.length : 0;
    const avgHayKgEq = activeEquines.length ? totalHayKgDay / activeEquines.length : 0;
    return { stockSacks, stockHayBales, weeklySacks, weeklyHayBales, avgFeedKgEq, avgHayKgEq };
  }, [activeEquines, selectedNutritionItems, rationItems, stockItems]);

  const latestWeightsByEquine = React.useMemo(()=>{
    const map = new Map();
    (weights||[]).forEach(item=>{
      const key = String(item?.equineId||'');
      if(!key) return;
      const prev = map.get(key);
      const prevAt = String(prev?.date || prev?.createdAt || '');
      const nextAt = String(item?.date || item?.createdAt || '');
      if(!prev || nextAt >= prevAt) map.set(key, item);
    });
    return map;
  }, [weights]);
  const equineActivityById = React.useMemo(()=>{
    const map = new Map();
    (equines||[]).forEach(eq=>{
      map.set(String(eq.id), inferEquineActivityLevel(schedule, eq.id, activityRules));
    });
    return map;
  }, [equines, schedule, activityRules]);

  useEffect(() => {
    const now = new Date().toISOString();
    let changed = false;
    const normalized = (equines||[]).map(item => {
      if(!item || typeof item !== "object") return item;
      let next = item;
      if(!next.id || typeof next.id !== "string"){ next = { ...next, id: createClientId("equ") }; changed = true; }
      if(!next.farmId){ next = { ...next, farmId: harasFarmId || "" }; changed = true; }
      if(!next.createdAt){ next = { ...next, createdAt: next.updatedAt || now }; changed = true; }
      if(!next.updatedAt){ next = { ...next, updatedAt: next.createdAt || now }; changed = true; }
      if(!next.updatedBy){ next = { ...next, updatedBy: myName || "" }; changed = true; }
      return next;
    });
    if(changed) setEquines(normalized);
  }, [equines, ensureHarasEquineRecordNumbers, harasFarmId, myName, setEquines]);

  useEffect(() => {
    const now = new Date().toISOString();
    let changed = false;
    const normalized = (stalls||[]).map(item => {
      if(!item || typeof item !== "object") return item;
      let next = item;
      if(!next.id || typeof next.id !== "string"){ next = { ...next, id: createClientId("stall") }; changed = true; }
      if(!next.farmId){ next = { ...next, farmId: harasFarmId || "" }; changed = true; }
      if(!next.createdAt){ next = { ...next, createdAt: next.updatedAt || now }; changed = true; }
      if(!next.updatedAt){ next = { ...next, updatedAt: next.createdAt || now }; changed = true; }
      if(!next.updatedBy){ next = { ...next, updatedBy: myName || "" }; changed = true; }
      return next;
    });
    if(changed) setStalls(normalized);
  }, [ensureHarasStallRecordNumbers, harasFarmId, myName, setStalls, stalls]);

  useEffect(() => {
    const now = new Date().toISOString();
    let changed = false;
    const normalized = (schedule||[]).map(item => {
      if(!item || typeof item !== "object") return item;
      let next = item;
      if(!next.id || typeof next.id !== "string"){ next = { ...next, id: createClientId("hsch") }; changed = true; }
      if(!next.farmId){ next = { ...next, farmId: harasFarmId || "" }; changed = true; }
      if(!next.createdAt){ next = { ...next, createdAt: next.updatedAt || now }; changed = true; }
      if(!next.updatedAt){ next = { ...next, updatedAt: next.createdAt || now }; changed = true; }
      if(!next.updatedBy){ next = { ...next, updatedBy: myName || "" }; changed = true; }
      return next;
    });
    if(changed) setSchedule(normalized);
  }, [schedule, harasFarmId, myName, setSchedule]);


  useEffect(() => {
    const now = new Date().toISOString();
    let changed = false;
    const normalized = (nutrition||[]).map(item => {
      if(!item || typeof item !== "object") return item;
      let next = item;
      if(!next.id || typeof next.id !== "string"){ next = { ...next, id: createClientId("hnut") }; changed = true; }
      if(!next.farmId){ next = { ...next, farmId: harasFarmId || "" }; changed = true; }
      if(!Array.isArray(next.feedTimes)){ next = { ...next, feedTimes: [] }; changed = true; }
      if(!next.createdAt){ next = { ...next, createdAt: next.updatedAt || now }; changed = true; }
      if(!next.updatedAt){ next = { ...next, updatedAt: next.createdAt || now }; changed = true; }
      if(!next.updatedBy){ next = { ...next, updatedBy: myName || "" }; changed = true; }
      return next;
    });
    if(changed) setNutrition(normalized);
  }, [nutrition, harasFarmId, myName, setNutrition]);


  useEffect(() => {
    const now = new Date().toISOString();
    let changed = false;
    const normalized = (weights||[]).map(item => {
      if(!item || typeof item !== "object") return item;
      let next = item;
      if(!next.id || typeof next.id !== "string"){ next = { ...next, id: createClientId("hwt") }; changed = true; }
      if(!next.farmId){ next = { ...next, farmId: harasFarmId || "" }; changed = true; }
      if(!next.createdAt){ next = { ...next, createdAt: next.updatedAt || now }; changed = true; }
      if(!next.updatedAt){ next = { ...next, updatedAt: next.createdAt || now }; changed = true; }
      if(!next.updatedBy){ next = { ...next, updatedBy: myName || "" }; changed = true; }
      return next;
    });
    if(changed) setWeights(normalized);
  }, [weights, harasFarmId, myName, setWeights]);

  // ── Equine form ─────────────────────────────────────────────────
  const defEquine = {
    _new:true, name:"", breed:activeBreeds[0]||HARAS_BREEDS[0], registration:"", sex:"Macho",
    birthDate:"", color:"", weight:"", height:"",
    origin:"", owner:"", trainer:"",
    photo:"", status:"Ativo",
    diet:[], dietNotes:"",
    notes:"",
    stallId:"",  // baia vinculada
  };

  function saveEquine() {
    if(!form.name.trim()){show("Nome obrigatório.","error");return;}
    const{_new,...d}=form;
    const newId = _new ? (d.id || createClientId("equ")) : String(d.id || createClientId("equ"));
    const equineBase = {...d, id:newId, farmId:d.farmId||harasFarmId||""};

    if(_new){
      setEquines(p=>dedupeById([...(p||[]), makeHarasCreatePending({...equineBase, ...makeCreateMeta(myName)}, p||[], "equ")]));
    } else {
      setEquines(p=>dedupeById((p||[]).map(e=>String(e.id)===String(newId)?makeHarasUpdatePending({...e,...equineBase, createdAt:e.createdAt||equineBase.createdAt, ...makeUpdateMeta(myName)}, "update"):e)));
    }

    // Sync stall assignment — push immediately to server
    if(form.stallId){
      const stallMeta = makeUpdateMeta(myName);
      const updatedStalls = stalls.map(s=>{
        const newEqId = String(s.id)===String(form.stallId)
          ? newId
          : String(s.equineId)===String(newId) ? "" : s.equineId;
        if(String(newEqId)===String(s.equineId)) return s;
        return {...s, equineId: newEqId, ...stallMeta};
      });
      setStalls(updatedStalls);
      // Push harasStalls immediately so other devices get the equineId right away
      if(harasFarmId && navigator.onLine){
        (async()=>{
          try {
            const key = `fz:${harasFarmId}:harasStalls`;
            await DB.set(key, updatedStalls);
            const locVer = (await DB.get(`${key}:_ver`)) || 0;
            const res = await pushCollection(harasFarmId, "harasStalls", updatedStalls, locVer, myName);
            if(res?.version) await DB.set(`${key}:_ver`, res.version);
            window.dispatchEvent(new CustomEvent("fz:sync:updated",{detail:{key}}));
          } catch(e){ console.warn("harasStalls push failed", e); }
        })();
      } else {
        syncOnSave("harasStalls");
      }
    } else if(!_new) {
      // If editing and stallId cleared, remove from any stall
      setStalls(p=>p.map(s=>String(s.equineId)===String(newId)
        ? ({...s, equineId:"", ...makeUpdateMeta(myName)})
        : s
      ));
      syncOnSave("harasStalls");
    }
    show("✅ Salvo!"); setForm(null); syncOnSave("harasEquines");
  }

  // ── Stall form ──────────────────────────────────────────────────
  const defStall = {
    _new:true, prefix:"Baia", suffix:nextBaiaSuffix, name:"", pastureId:"", equineId:"",
    status:"Limpa",
    water: "Sem Avaliação",
    trough:"Sem Avaliação",
    bedThickness:"Em Conformidade",
    bedMoisture:"Seca",
    lastInspection:null, lastInspector:"",
    inspectionPhoto:"",
    repairs:[],       // [{id, item, description, requestedBy, requestedAt, status, adminNote, resolvedAt}]
    history:[],       // [{ts, inspector, status, water, trough, bedThickness, bedMoisture, notes, photo}]
  };

  function saveStall() {
    const suffix = String(stallForm.suffix || "").trim() || nextBaiaSuffix;
    const fullName = `Baia ${suffix}`;
    const pastureList = Array.isArray(pastures) ? pastures : [];
    const linkedPasture = pastureList.find(p=>String(p.id)===String(stallForm.pastureId||""));
    const {_new, ...raw} = stallForm;
    const base = { ...raw, prefix:"Baia", suffix, name:fullName, sector: linkedPasture?.name || "", pastureIds: stallForm.pastureId ? [stallForm.pastureId] : [] };
    const stallId = base.id || createClientId("stall");
    const poiId = base.poiId || `poi_baia_${stallId}`;
    const poiPayload = {
      id: poiId,
      linkedModule:"haras",
      linkedType:"stall",
      linkedStallId: stallId,
      category:"Baia",
      name: fullName,
      costCenter:"Haras",
      status:"Ativo",
      pastureIds: base.pastureId ? [base.pastureId] : [],
      farmId: harasFarmId || "",
      notes: base.notes || "",
    };
    if(_new) {
      setStalls(p=>dedupeById([...(p||[]), makeHarasCreatePending({...base,id:stallId,poiId,farmId:harasFarmId||base.farmId||"",...makeCreateMeta(myName)}, p||[], "stall")]));
      setPois(p=>dedupeById([...p,{...poiPayload,...makeCreateMeta(myName)}]));
    } else {
      setStalls(p=>dedupeById((p||[]).map(s=>String(s.id)===String(stallId)?makeHarasUpdatePending({...s,...base,id:stallId,poiId,farmId:base.farmId||s.farmId||harasFarmId||"",createdAt:s.createdAt||base.createdAt,...makeUpdateMeta(myName)}, "update"):s)));
      setPois(p=>dedupeById(p.map(item=>String(item.id)===String(poiId)?{...item,...poiPayload,createdAt:item.createdAt||poiPayload.createdAt,...makeUpdateMeta(myName)}:item)));
    }
    show("✅ Baia salva!"); setStallForm(null); syncOnSave("harasStalls"); syncOnSave("poi");
  }

  function removeStall(stall) {
    if (!stall) return;
    const targetId = String(stall.id || "");
    const targetPoiId = String(stall.poiId || `poi_baia_${targetId}`);
    setStalls(prev => dedupeById((Array.isArray(prev) ? prev : []).map(item => String(item?.id || "") === targetId ? makeHarasUpdatePending({ ...item, _deleted:true, deletedAt:new Date().toISOString(), ...makeUpdateMeta(myName) }, "delete") : item)));
    setPois(prev => dedupeById((Array.isArray(prev) ? prev : []).filter(item => String(item?.id || "") !== targetPoiId && String(item?.linkedStallId || "") !== targetId)));
    if (detailStall && String(detailStall.id || "") === targetId) setDetailStall(null);
    show("🗑️ Baia removida.");
    syncOnSave("harasStalls");
    syncOnSave("poi");
  }

  // ── Inspection (collaborator) ───────────────────────────────────
  function getInspSeverity(data) {
    if(data.status==="Não Conforme"||data.water==="Suja"||data.trough==="Danificado"||data.bedMoisture==="Molhada")
      return "red";
    if(data.status==="Suja"||data.water==="Vazia"||data.bedThickness==="Fina Demais"||data.bedMoisture==="Úmida")
      return "orange";
    return "green";
  }

  function submitInspection(stall, data) {
    const severity = getInspSeverity(data);
    const entry = {
      ts:new Date().toISOString(), inspector:myName,
      status:data.status, water:data.water, trough:data.trough,
      bedThickness:data.bedThickness, bedMoisture:data.bedMoisture,
      notes:data.notes||"", photo:data.photo||"",
      severity,
    };
    const updated = {
      ...stall, ...data,
      lastInspection:entry.ts, lastInspector:myName,
      lastSeverity:severity,
      history:[...(stall.history||[]),ensureHarasSubEntry(entry, "hins")],
      ...makeUpdateMeta(myName),
    };
    setStalls(p=>dedupeById((p||[]).map(s=>s.id===stall.id?makeHarasUpdatePending(updated, "inspection"):s)));
    setDetailStall(updated);
    syncOnSave("harasStalls");
    show("✅ Vistoria registrada!");
  }

  // ── Repair request ──────────────────────────────────────────────
  function requestRepair(stall, item, description) {
    const repair = ensureHarasSubEntry({
      id: createClientId("hrep"), item, description,
      requestedBy:myName, requestedAt:new Date().toISOString(),
      status:"Pendente", adminNote:"", resolvedAt:null,
    }, "hrep");
    const updated = {...stall, repairs:[...(stall.repairs||[]),repair], ...makeUpdateMeta(myName)};
    setStalls(p=>dedupeById((p||[]).map(s=>s.id===stall.id?makeHarasUpdatePending(updated, "inspection"):s)));
    setDetailStall(updated);
    syncOnSave("harasStalls");
    show("📋 Pedido de reparo enviado!");
  }

  // ── Admin review repair ─────────────────────────────────────────
  function reviewRepair(stall, repairId, newStatus, adminNote) {
    const updated = {
      ...stall,
      repairs:(stall.repairs||[]).map(r=>r.id===repairId
        ? ensureHarasSubEntry({...r, status:newStatus, adminNote, resolvedAt:new Date().toISOString()}, "hrep")
        : r),
      ...makeUpdateMeta(myName)
    };
    setStalls(p=>dedupeById((p||[]).map(s=>s.id===stall.id?makeHarasUpdatePending(updated, "inspection"):s)));
    setDetailStall(updated);
    syncOnSave("harasStalls");
    show(`✅ ${newStatus}!`);
  }

  // ── Schedule helpers ───────────────────────────────────────────
  function saveScheduleItem() {
    if(!schedForm.equineId||!schedForm.type){show("Selecione o equino e o tipo de treino.","error");return;}
    const{_new,...d}=schedForm;
    if(_new) setSchedule(p=>dedupeById([...(p||[]), makeHarasCreatePending({...d,id:d.id||createClientId("hsch"),farmId:harasFarmId||d.farmId||"",...makeCreateMeta(myName)}, p||[], "hsch")]));
    else     setSchedule(p=>dedupeById((p||[]).map(s=>String(s.id)===String(d.id)?makeHarasUpdatePending({...d,farmId:d.farmId||s.farmId||harasFarmId||"",createdAt:s.createdAt||d.createdAt,...makeUpdateMeta(myName)}, "update"):s)));
    show("✅ Agendado!"); setSchedForm(null); syncOnSave("harasSchedule");
  }

  function saveNutritionPlan() {
    if(!nutritionForm?.equineId){ show("Selecione o equino.","error"); return; }
    if(!nutritionForm?.feedStockId && !nutritionForm?.supplementStockId && !nutritionForm?.hayStockId){ show("Informe ao menos uma ração, feno ou suplementação.","error"); return; }
    const current = (nutrition||[]).find(n=>String(n.id)===String(nutritionForm.id));
    const eq = (equines||[]).find(item=>String(item.id)===String(nutritionForm.equineId));
    const activityInfo = inferEquineActivityLevel(schedule, nutritionForm.equineId, activityRules);
    const breedRule = getBreedRule(breedRules, eq?.breed);
    const recommendation = suggestNutritionByEquine(eq, activityInfo, breedRule);
    const hayRecommendation = suggestHayByEquine(eq, activityInfo, breedRule, nutritionForm.hayQuality || "Boa");
    const chosenPct = num(nutritionForm.feedPctBW, 0);
    if(chosenPct && (chosenPct < recommendation.minPctBW || chosenPct > recommendation.maxPctBW)){
      const shouldContinue = typeof window !== 'undefined' ? window.confirm(`O valor de ${chosenPct.toFixed(2)}% do peso vivo está fora da faixa sugerida (${recommendation.minPctBW.toFixed(2)}% a ${recommendation.maxPctBW.toFixed(2)}%) para este equino. Deseja continuar mesmo assim?`) : true;
      if(!shouldContinue) return;
    }
    const payload = {
      ...nutritionForm,
      id: nutritionForm.id || createClientId("hnut"),
      farmId: harasFarmId || nutritionForm.farmId || "",
      feedTimes: Array.from(new Set((nutritionForm.feedTimes||[]).filter(Boolean))).sort(),
      forageTimes: Array.from(new Set((nutritionForm.forageTimes||[]).filter(Boolean))).sort(),
      hayQuality: nutritionForm.hayQuality || "Boa",
      forageMealMode: nutritionForm.forageMealMode || "general",
      activityLevelAuto: activityInfo.levelName,
      activitySessionsWeek: activityInfo.totalSessions,
      recommendedPctBW: recommendation.suggestedPctBW,
      recommendedPctRange: `${recommendation.minPctBW}-${recommendation.maxPctBW}`,
      recommendedHayPctBW: hayRecommendation.suggestedPctBW,
      recommendedHayPctRange: `${hayRecommendation.minPctBW}-${hayRecommendation.maxPctBW}`,
    };
    if(current){
      setNutrition(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(payload.id)?makeHarasUpdatePending({...current,...payload,createdAt:current.createdAt||payload.createdAt,...makeUpdateMeta(myName)}, "update"):item)));
    } else {
      setNutrition(prev=>dedupeById([...(prev||[]), makeHarasCreatePending({...payload,...makeCreateMeta(myName)}, prev||[], "hnut")]));
    }
    show("✅ Nutrição atualizada!");
    setNutritionForm(null);
    syncOnSave("harasNutrition");
  }

  function saveFeedTimesPlan() {
    if(!feedTimesForm?.equineId){ show("Selecione o equino.","error"); return; }
    const current = nutritionByEquine.get(String(feedTimesForm.equineId));
    const payload = {
      ...(current||{}),
      id: current?.id || createClientId("hnut"),
      equineId: feedTimesForm.equineId,
      farmId: harasFarmId || current?.farmId || "",
      feedStockId: current?.feedStockId || "",
      feedQty: current?.feedQty || "",
      supplementStockId: current?.supplementStockId || "",
      supplementQty: current?.supplementQty || "",
      hayStockId: current?.hayStockId || "",
      forageQty: current?.forageQty || "",
      notes: current?.notes || "",
      mealMode: feedTimesForm.mealMode || "general",
      forageMealMode: feedTimesForm.forageMealMode || current?.forageMealMode || "general",
      feedTimes: Array.from(new Set((feedTimesForm.feedTimes||[]).filter(Boolean))).sort(),
      forageTimes: Array.from(new Set((feedTimesForm.forageTimes||[]).filter(Boolean))).sort(),
    };
    if(current){
      setNutrition(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(current.id)?makeHarasUpdatePending({...payload,createdAt:current.createdAt||payload.createdAt,...makeUpdateMeta(myName)}, "update"):item)));
    } else {
      setNutrition(prev=>dedupeById([...(prev||[]), makeHarasCreatePending({...payload,...makeCreateMeta(myName)}, prev||[], "hnut")]));
    }
    show("✅ Horários atualizados!");
    setFeedTimesForm(null);
    syncOnSave("harasNutrition");
  }

  function saveWeightEntry() {
    if(!weightForm?.equineId){ show("Selecione o equino.","error"); return; }
    if(!weightForm?.date){ show("Informe a data da pesagem.","error"); return; }
    if(!weightForm?.weightKg){ show("Informe o peso.","error"); return; }
    const current = (weights||[]).find(item=>String(item.id)===String(weightForm.id));
    const payload = {
      ...weightForm,
      id: weightForm.id || createClientId('hwt'),
      farmId: harasFarmId || weightForm.farmId || '',
    };
    if(current){
      setWeights(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(payload.id)?makeHarasUpdatePending({...current,...payload,createdAt:current.createdAt||payload.createdAt,...makeUpdateMeta(myName)}, "update"):item)));
    } else {
      setWeights(prev=>dedupeById([...(prev||[]), makeHarasCreatePending({...payload,...makeCreateMeta(myName)}, prev||[], "hwt")]));
    }
    const latest = num(payload.weightKg, 0);
    if(latest>0){
      setEquines(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(payload.equineId)?makeHarasUpdatePending({...item,weight:String(latest),...makeUpdateMeta(myName)}, "weight"):item)));
      syncOnSave('harasEquines');
    }
    show('✅ Pesagem registrada!');
    setWeightForm(null);
    syncOnSave('harasWeights');
  }

  function saveManagementItem() {
    if(!managementForm?.equineId || !managementForm?.managementType){ show("Selecione o equino e o manejo.","error"); return; }
    if(!managementForm?.plannedDate){ show("Informe a data do manejo.","error"); return; }
    const equine = (equines||[]).find(eq=>String(eq.id)===String(managementForm.equineId));
    const existing = (services||[]).find(s=>String(s.id)===String(managementForm.id));
    const plannedTimes = Array.from(new Set((managementForm.plannedTimes||[]).filter(Boolean))).sort();
    const base = {
      ...(existing||{}),
      id: managementForm.id || createClientId("svc"),
      type: "internal",
      title: managementForm.managementType,
      description: managementForm.description || `${managementForm.managementType}${equine?.name?` — ${equine.name}`:""}`,
      status: managementForm.status || existing?.status || "Aberto",
      date: managementForm.plannedDate,
      category: existing?.category || "other",
      location: managementForm.location || existing?.location || "Haras",
      notes: managementForm.notes || "",
      assignees: managementForm.assignees || existing?.assignees || [],
      farmId: harasFarmId || existing?.farmId || "",
      harasAgendaType: "manejo",
      harasManagementType: managementForm.managementType,
      equineId: managementForm.equineId,
      equineName: equine?.name || managementForm.equineName || existing?.equineName || "",
      plannedDate: managementForm.plannedDate,
      plannedTimes,
      serviceOrigin: "haras",
      timeline: existing?.timeline || [],
    };
    const serviceNumberParts = existing ? null : buildNextRecordNumber(services||[], { moduleKey:"services" });
    const servicePrvNumber = serviceNumberParts ? appendProvisionalSuffix(serviceNumberParts.recordNumber || "") : "";
    const next = existing
      ? markOperationalUpdatePending({ ...base, createdAt: existing.createdAt || base.createdAt, ...makeUpdateMeta(myName) }, { userName: myName || "", operationType:"update" })
      : markOperationalCreatePending({ ...base, ...serviceNumberParts, recordNumber: servicePrvNumber, provisionalRecordNumber: servicePrvNumber, provisional:true, recordNumberStatus:"provisional", ...makeCreateMeta(myName), timeline:[ensureHarasSubEntry({ ts:new Date().toISOString(), actor:myName, action:"Manejo criado no Haras", note: `${managementForm.managementType}${equine?.name?` · ${equine.name}`:""}` }, "htl")] }, { userName: myName || "" });
    if(existing) {
      setServices(prev=>dedupeById(prev.map(item=>String(item.id)===String(existing.id)?next:item)));
    } else {
      setServices(prev=>dedupeById([...prev,next]));
    }
    show("✅ Manejo salvo e vinculado como serviço interno!");
    setManagementForm(null);
    syncOnSave("services");
  }

  const dirtyStalls    = stalls.filter(s=>s.status==="Suja"||s.status==="Não Conforme").length;
  const { startOfWeek, endOfWeek, startOfMonth, endOfMonth } = React.useMemo(()=>{
    const now = new Date();
    const sow = new Date(now);
    sow.setHours(0,0,0,0);
    sow.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const eow = new Date(sow);
    eow.setDate(sow.getDate() + 7);
    const som = new Date(now.getFullYear(), now.getMonth(), 1);
    const eom = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { startOfWeek: sow, endOfWeek: eow, startOfMonth: som, endOfMonth: eom };
  }, []);
  const inRange = (value, start, end)=>{
    if(!value) return false;
    const date = new Date(String(value).length <= 10 ? `${value}T00:00:00` : value);
    if(Number.isNaN(date.getTime())) return false;
    return date >= start && date < end;
  };
  const agendaStats = React.useMemo(()=>({
    treinosAgendadosSemana: (schedule||[]).filter(item=> item?.day !== undefined && item?.day !== null).length,
    manejosAgendadosSemana: (managementServices||[]).filter(item=>inRange(item?.plannedDate || item?.date, startOfWeek, endOfWeek)).length,
    treinosRealizadosSemana: (schedule||[]).filter(item=> String(item?.status||"").toLowerCase()==="concluido" || String(item?.status||"").toLowerCase()==="realizado").length,
    manejosRealizadosSemana: (managementServices||[]).filter(item=> (String(item?.status||"").toLowerCase()==="concluido" || String(item?.status||"").toLowerCase()==="realizado") && inRange(item?.plannedDate || item?.date, startOfWeek, endOfWeek)).length,
  }), [schedule, managementServices, startOfWeek, endOfWeek]);
  const stallsStats = React.useMemo(()=>({
    baiasConformesSemana: (stalls||[]).filter(item=> String(item?.status||"") === "Conforme").length,
    baiasNaoConformesSemana: (stalls||[]).filter(item=> ["Não Conforme","Suja"].includes(String(item?.status||""))).length,
    vistoriasAgendadasMensal: (stalls||[]).reduce((sum, item)=> sum + ((item?.scheduledInspections||[]).filter(entry=>inRange(entry?.date, startOfMonth, endOfMonth)).length), 0),
    vistoriasRealizadasMensal: (stalls||[]).reduce((sum, item)=> sum + ((item?.inspections||[]).filter(entry=>inRange(entry?.date || entry?.createdAt, startOfMonth, endOfMonth)).length), 0),
  }), [stalls, startOfMonth, endOfMonth]);
  const rootCardIds = React.useMemo(()=>["numero_equinos","numero_garanhoes","numero_eguas","numero_baias"], []);
  const nutritionCardIds = React.useMemo(()=>["estoque_racao","consumo_sacos_racao_semanal","consumo_racao_animal_dia","estoque_feno","consumo_feno_semanal","consumo_feno_animal_dia"], []);
  const agendaCardIds = React.useMemo(()=>["treinos_agendados_semana","manejos_agendados_semana","treinos_realizados_semana","manejos_realizados_semana"], []);
  const baiasCardIds = React.useMemo(()=>["baias_conformes_semana","baias_nao_conformes_semana","vistorias_agendadas_mensal","vistorias_realizadas_mensal"], []);
  const allowedHomeCards = React.useMemo(()=>getConfiguredSectionCards("moduleHome", rootCardIds), [getConfiguredSectionCards, rootCardIds]);
  const allowedPlantelCards = React.useMemo(()=>getConfiguredSectionCards("menu:plantel", rootCardIds), [getConfiguredSectionCards, rootCardIds]);
  const allowedNutritionCards = React.useMemo(()=>getConfiguredSectionCards("menu:nutricao", nutritionCardIds), [getConfiguredSectionCards, nutritionCardIds]);
  const allowedAgendaCards = React.useMemo(()=>getConfiguredSectionCards("menu:agenda", agendaCardIds), [getConfiguredSectionCards, agendaCardIds]);
  const allowedBaiasCards = React.useMemo(()=>getConfiguredSectionCards("menu:baias", baiasCardIds), [getConfiguredSectionCards, baiasCardIds]);

  const renderEquineForm = ()=> form && <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>

              <div style={{gridColumn:"1/-1",display:"flex",gap:16,alignItems:"flex-start",flexWrap:isCompactViewport()?"wrap":"nowrap"}}>
                <div style={{width:120,height:120,borderRadius:12,background:`${CC_COLORS["Haras"]}18`,border:`2px dashed ${CC_COLORS["Haras"]}44`,overflow:"hidden",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",position:"relative"}}
                  onClick={()=>document.getElementById("equine-photo-input").click()}>
                  {form.photo
                    ? <img src={form.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    : <HorseIcon size={52} />
                  }
                  <div style={{position:"absolute",bottom:4,right:4,background:CC_COLORS["Haras"],borderRadius:6,padding:"2px 6px",fontSize:10,color:"#fff"}}>📷</div>
                </div>
                <input id="equine-photo-input" type="file" accept="image/*" style={{display:"none"}}
                  onChange={e=>{
                    const f2=e.target.files[0]; if(!f2) return;
                    const r=new FileReader(); r.onload=ev=>setForm(f=>({...f,photo:ev.target.result})); r.readAsDataURL(f2);
                  }}/>
                <div style={{flex:1,minWidth:isCompactViewport()?"100%":0}}>
                  <Field label="Nome *"><Input value={form.name} placeholder="Nome do equino" onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></Field>
                </div>
              </div>

              <Field label="Raça">
                <Select value={form.breed} onChange={e=>setForm(f=>({...f,breed:e.target.value}))}>
                  {activeBreeds.map(b=><option key={b}>{b}</option>)}
                </Select>
              </Field>
              <Field label="Registro / Passaporte">
                <Input value={form.registration||""} placeholder="Nº de registro" onChange={e=>setForm(f=>({...f,registration:e.target.value}))}/>
              </Field>
              <Field label="Sexo">
                <Select value={form.sex} onChange={e=>setForm(f=>({...f,sex:e.target.value}))}>
                  {["Macho","Fêmea","Castrado"].map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                  {["Ativo","Em tratamento","Aposentado","Vendido","Óbito"].map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Data de Nascimento">
                <Input type="date" value={form.birthDate||""} onChange={e=>setForm(f=>({...f,birthDate:e.target.value}))}/>
              </Field>
              <Field label="Pelagem / Cor">
                <Select value={form.color||""} onChange={e=>setForm(f=>({...f,color:e.target.value}))}>
                  <option value="">Selecione a pelagem...</option>
                  {activeCoats.map(c=>(<option key={c.name} value={c.name}>{c.name}</option>))}
                </Select>
                {form.color&&(()=>{ const coat=activeCoats.find(c=>c.name===form.color); return coat?(<div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}><div style={{width:20,height:20,borderRadius:4,background:coat.hex,border:`1px solid ${T.border}`}}/><span style={{fontSize:11,color:T.muted}}>{coat.hex}</span></div>):null; })()}
              </Field>
              <Field label="Peso (kg)"><Input type="number" value={form.weight||""} onChange={e=>setForm(f=>({...f,weight:e.target.value}))}/></Field>
              <Field label="Altura (mãos/cm)"><Input value={form.height||""} placeholder="Ex: 15.2 mãos / 160 cm" onChange={e=>setForm(f=>({...f,height:e.target.value}))}/></Field>
              <Field label="Origem / Criador"><Input value={form.origin||""} placeholder="Haras de origem..." onChange={e=>setForm(f=>({...f,origin:e.target.value}))}/></Field>
              <Field label="Proprietário"><><Input list="haras-owner-people" value={form.owner||""} onChange={e=>setForm(f=>({...f,owner:e.target.value}))}/><datalist id="haras-owner-people">{ownerPeople.map(person=><option key={person.id} value={person.name}>{person.tradeName?`${person.tradeName} · proprietário`:"Proprietário"}</option>)}</datalist></></Field>
              <Field label="Treinador" col="1/-1"><Input value={form.trainer||""} onChange={e=>setForm(f=>({...f,trainer:e.target.value}))}/></Field>
              <Field label="🏠 Baia" col="1/-1">
                <Select value={form.stallId||""} onChange={e=>setForm(f=>({...f,stallId:e.target.value}))}>
                  <option value="">Sem baia definida</option>
                  {sortedStalls.map(s=>{ const ocupante=sortedEquines.find(eq=>eq.id===s.equineId&&eq.id!==form.id); return (<option key={s.id} value={s.id} disabled={!!ocupante}>{s.name}{s.sector?` — ${s.sector}`:""}{ocupante?` (ocupada por ${ocupante.name})`:""}</option>); })}
                </Select>
                {stalls.length===0&&<div style={{fontSize:11,color:T.yellow,marginTop:4}}>⚠️ Nenhuma baia cadastrada ainda. Crie baias na aba Baias.</div>}
              </Field>
              <div style={{gridColumn:"1/-1",borderTop:`1px solid ${T.border}`,paddingTop:14}}>
                <div style={{fontWeight:700,color:T.green,fontSize:13,marginBottom:10}}><span style={{display:"inline-flex",alignItems:"center",gap:6}}><EquineRationBagIcon size={19} /> Dieta Individual</span></div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>{DIET_ITEMS.map(item=>{ const sel=(form.diet||[]).includes(item); return (<div key={item} onClick={()=>setForm(f=>({...f,diet:sel?(f.diet||[]).filter(d=>d!==item):[...(f.diet||[]),item]}))} style={{padding:"5px 12px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:sel?700:400,transition:"all .15s",background:sel?`${T.green}22`:T.card2,border:`1px solid ${sel?T.green:T.border}`,color:sel?T.green:T.muted}}>{item}</div>); })}</div>
                <Field label="Observações sobre a dieta" col="1/-1"><Textarea rows={2} value={form.dietNotes||""} placeholder="Quantidades, horários, restrições alimentares..." onChange={e=>setForm(f=>({...f,dietNotes:e.target.value}))}/></Field>
              </div>
              <Field label="Observações Gerais" col="1/-1"><Textarea rows={2} value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/></Field>
              <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
                <div>{!form._new&&canEdit&&<Btn variant="danger" onClick={()=>{setEquines(p=>dedupeById((p||[]).map(e=>e.id===form.id?makeHarasUpdatePending({...e,_deleted:true,deletedAt:new Date().toISOString(),...makeUpdateMeta(myName)}, "delete"):e)));setForm(null);show("🗑️ Removido.");syncOnSave("harasEquines");}}>🗑️ Excluir</Btn>}</div>
                <div style={{display:"flex",gap:10,marginLeft:"auto"}}><Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>{canEdit&&<Btn onClick={saveEquine}>Salvar</Btn>}</div>
              </div>
            </div>;

  if(!canViewHaras){
    return <div><Toast toast={toast}/><div style={{background:`${T.red}12`,border:`1px solid ${T.red}33`,borderRadius:14,padding:"24px 18px",textAlign:"center",color:T.text}}><div style={{fontSize:36,marginBottom:8}}>🔒</div><div style={{fontWeight:800,marginBottom:6}}>Sem acesso ao centro de custo Haras</div><div style={{fontSize:13,color:T.muted}}>Peça ao administrador para liberar visualização do Haras para sua conta.</div></div></div>;
  }

  return(
    <div><Toast toast={toast}/>
      {!!deniedCostCenters.length && <div style={{background:`${T.yellow}12`,border:`1px solid ${T.yellow}33`,borderRadius:12,padding:"10px 14px",marginBottom:14,fontSize:12,color:T.text}}>⚠️ Sem acesso a: <strong>{deniedCostCenters.join(", ")}</strong>. O Haras permanece focado apenas no centro permitido.</div>}

      {/* Stats */}
      {(()=>{
        const sectionId = cardsSection || "moduleHome";
        let statCards = [];
        if(sectionId === "menu:agenda"){
          statCards = [
            { id:"treinos_agendados_semana", icon:"🐴", label:"Treinos Agendados", value:agendaStats.treinosAgendadosSemana, color:CC_COLORS["Haras"] },
            { id:"manejos_agendados_semana", icon:"🧹", label:"Manejos Agendados", value:agendaStats.manejosAgendadosSemana, color:T.yellow },
            { id:"treinos_realizados_semana", icon:"✅", label:"Treinos Realizados", value:agendaStats.treinosRealizadosSemana, color:T.green },
            { id:"manejos_realizados_semana", icon:"📌", label:"Manejos Realizados", value:agendaStats.manejosRealizadosSemana, color:T.accent },
          ].filter(card=>allowedAgendaCards.includes(card.id));
        } else if(sectionId === "menu:baias"){
          statCards = [
            { id:"baias_conformes_semana", icon:"🏠", label:"Baias Conformes", value:stallsStats.baiasConformesSemana, color:T.green, iconPosition:"topRight", iconWrapStyle:{width:60,height:60,minWidth:60,top:14,right:16}, iconStyle:{fontSize:30}, contentPadRight:108 },
            { id:"baias_nao_conformes_semana", icon:"⚠️", label:"Baias Não Conformes", value:stallsStats.baiasNaoConformesSemana, color:T.red, iconPosition:"topRight", iconWrapStyle:{width:60,height:60,minWidth:60,top:14,right:16}, iconStyle:{fontSize:30}, contentPadRight:108 },
            { id:"vistorias_agendadas_mensal", icon:"📅", label:"Vistorias Agendadas", value:stallsStats.vistoriasAgendadasMensal, color:T.yellow, iconPosition:"topRight", iconWrapStyle:{width:60,height:60,minWidth:60,top:14,right:16}, iconStyle:{fontSize:30}, contentPadRight:108 },
            { id:"vistorias_realizadas_mensal", icon:"📝", label:"Vistorias Realizadas", value:stallsStats.vistoriasRealizadasMensal, color:T.accent, iconPosition:"topRight", iconWrapStyle:{width:60,height:60,minWidth:60,top:14,right:16}, iconStyle:{fontSize:30}, contentPadRight:108 },
          ].filter(card=>allowedBaiasCards.includes(card.id));
        } else if(sectionId === "menu:nutricao"){
          statCards = [
            {id:"estoque_racao", icon:renderCardImageIcon(equineRationCardIcon, "Estoque de ração equinos"), label:"Estoque Ração", value:formatStatValue(nutritionStats.stockSacks.toFixed(1), "sacos"), color:T.accent, iconPosition:"topRight", iconWrapStyle:{width:74,height:74,minWidth:74,top:12,right:14}, iconStyle:{width:67,height:67,objectFit:"contain"}, contentPadRight:124},
            {id:"consumo_sacos_racao_semanal", icon:renderCardImageIcon(equineRationCardIcon, "Consumo semanal de ração equinos"), label:"Consumo Semanal", value:formatStatValue(nutritionStats.weeklySacks.toFixed(1), "sc/sem"), color:T.yellow, iconPosition:"topRight", iconWrapStyle:{width:74,height:74,minWidth:74,top:12,right:14}, iconStyle:{width:82,height:82,objectFit:"contain"}, contentPadRight:124},
            {id:"consumo_racao_animal_dia", icon:renderCardImageIcon(equineRationCardIcon, "Consumo animal ração dia equinos"), label:"Consumo Diário", value:formatStatValue(nutritionStats.avgFeedKgEq.toFixed(2), "kg/eq"), color:T.orange || T.accent, iconPosition:"topRight", iconWrapStyle:{width:74,height:74,minWidth:74,top:12,right:14}, iconStyle:{width:82,height:82,objectFit:"contain"}, contentPadRight:124},
            {id:"estoque_feno", icon:renderCardImageIcon(harasHayCardIcon, "Estoque de feno"), label:"Estoque Feno", value:formatStatValue(nutritionStats.stockHayBales.toFixed(1), "fardos"), color:T.green, iconPosition:"topRight", iconWrapStyle:{width:74,height:74,minWidth:74,top:12,right:14}, iconStyle:{width:82,height:82,objectFit:"contain"}, contentPadRight:124},
            {id:"consumo_feno_semanal", icon:renderCardImageIcon(harasHayCardIcon, "Consumo semanal de feno"), label:"Cons Sem Feno", value:formatStatValue(nutritionStats.weeklyHayBales.toFixed(1), "fd/sem"), color:CC_COLORS["Haras"], iconPosition:"topRight", iconWrapStyle:{width:74,height:74,minWidth:74,top:12,right:14}, iconStyle:{width:82,height:82,objectFit:"contain"}, contentPadRight:124},
            {id:"consumo_feno_animal_dia", icon:renderCardImageIcon(harasHayCardIcon, "Consumo de feno por animal por dia"), label:"Cons Animal Feno Dia", value:formatStatValue(nutritionStats.avgHayKgEq.toFixed(2), "kg/eq"), color:T.muted, iconPosition:"topRight", iconWrapStyle:{width:74,height:74,minWidth:74,top:12,right:14}, iconStyle:{width:82,height:82,objectFit:"contain"}, contentPadRight:124},
          ].filter(card=>allowedNutritionCards.includes(card.id));
        } else {
          const allowedIds = sectionId === "menu:plantel" ? allowedPlantelCards : allowedHomeCards;
          statCards = [
            {id:"numero_equinos", icon:<HorseIcon size={26} />, label:"Equinos", value:activeEquines.length, color:CC_COLORS["Haras"], iconPosition:"topRight", iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{fontSize:32,width:40,height:40}},
            {id:"numero_garanhoes", icon:"♂️", label:"Garanhões", value:activeEquines.filter(e=>e.sex==="Macho").length, color:T.accent, iconPosition:"topRight", iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{fontSize:32}},
            {id:"numero_eguas", icon:"♀️", label:"Éguas", value:activeEquines.filter(e=>e.sex==="Fêmea").length, color:CC_COLORS["Haras"], iconPosition:"topRight", iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{fontSize:32}},
            {id:"numero_baias", icon:"🏠", label:"Baias", value:(stalls||[]).filter(item=>!item?._deleted).length, color:T.muted, iconPosition:"topRight", iconWrapStyle:{width:58,height:58,minWidth:58}, iconStyle:{fontSize:30}},
          ].filter(card=>allowedIds.includes(card.id));
        }
        if(!statCards.length) return null;
        const gridCols = sectionId === "menu:nutricao" ? "repeat(2,minmax(0,1fr))" : getVetStyleStatGridCols(statCards.length);
        return (
          <div style={{display:"grid",gridTemplateColumns:gridCols,gap:10,marginBottom:20}}>
            {statCards.map((card,idx)=>(
              <div key={`${card.id}-${idx}`} style={getVetStyleStatItemStyle(statCards.length, idx)}><StatCard {...card}/></div>
            ))}
          </div>
        );
      })()}

      {/* Tabs */}
      <ModuleHeaderMenu
        T={T}
        title="Menu do Haras"
        items={[
          {
            id:"agenda",
            label:"Agenda",
            icon:"📅",
            active:tab==="agenda",
            children:[
              { id:"agenda-horarios", label:"Horários", icon:"🕒", active:tab==="agenda" && agendaTab==="horarios", onClick:()=>{ setTab("agenda"); setAgendaTab("horarios"); setCardsSection("menu:agenda"); } },
              { id:"agenda-manejos", label:"Manejos", icon:"🧹", active:tab==="agenda" && agendaTab==="manejos", onClick:()=>{ setTab("agenda"); setAgendaTab("manejos"); setCardsSection("menu:agenda"); } },
              { id:"agenda-treinos", label:"Agenda", icon:"🐴", active:tab==="agenda" && agendaTab==="treinos", onClick:()=>{ setTab("agenda"); setAgendaTab("treinos"); setCardsSection("menu:agenda"); } },
            ],
          },
          { id:"stalls", label:"Baias", icon:"🏠", badge:dirtyStalls>0 ? `${dirtyStalls} ⚠️` : "", active:tab==="stalls", onClick:()=>{ setTab("stalls"); setCardsSection("menu:baias"); } },
          { id:"nutrition", label:"Nutrição", icon:"🥣", active:tab==="nutrition", onClick:()=>{ setTab("nutrition"); setCardsSection("menu:nutricao"); } },
          { id:"plantel", label:"Plantel", icon:"🐴", active:tab==="plantel", onClick:()=>{ setTab("plantel"); setCardsSection("menu:plantel"); } },
        ]}
      />

      {/* ─── PLANTEL TAB ──────────────────────────────────────── */}
      {tab==="plantel"&&(
        <>
          {canEdit&&<div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
            <Btn onClick={()=>setForm({...defEquine})}>+ Novo Equino</Btn>
          </div>}

          {equines.length===0&&(
            <div style={{color:T.muted,textAlign:"center",padding:48}}>
              Nenhum equino cadastrado no haras ainda.
            </div>
          )}

          {form && form._new && <div style={{marginBottom:16,background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
              <div style={{fontWeight:800,color:T.text,fontSize:18}}>Novo Equino no Haras</div>
              <Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn>
            </div>
            {renderEquineForm()}
          </div>}

          {weightForm && <div ref={weightFormRef} style={{marginBottom:16,background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>⚖️ Nova Pesagem</div><Btn variant="ghost" onClick={()=>setWeightForm(null)}>← Fechar</Btn></div><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}><Field label="Equino"><Select value={weightForm.equineId||''} onChange={e=>setWeightForm(f=>({...f,equineId:e.target.value}))}>{sortedEquines.map(eq=><option key={eq.id} value={eq.id}>{eq.name}</option>)}</Select></Field><Field label="Data"><Input type="date" value={weightForm.date||''} onChange={e=>setWeightForm(f=>({...f,date:e.target.value}))} /></Field><Field label="Peso vivo (kg)"><Input type="number" step="0.1" value={weightForm.weightKg||''} onChange={e=>setWeightForm(f=>({...f,weightKg:e.target.value}))} /></Field><Field label="Nível de atividade"><Input value={weightForm.activityLevel||''} onChange={e=>setWeightForm(f=>({...f,activityLevel:e.target.value}))} /></Field><Field label="Ração (kg/dia)"><Input type="number" step="0.01" value={weightForm.feedKgDay||''} onChange={e=>setWeightForm(f=>({...f,feedKgDay:e.target.value}))} /></Field><Field label="Feno / volumoso (kg/dia)"><Input type="number" step="0.01" value={weightForm.forageKgDay||''} onChange={e=>setWeightForm(f=>({...f,forageKgDay:e.target.value}))} /></Field><Field label="Suplementação (kg/dia)"><Input type="number" step="0.01" value={weightForm.supplementKgDay||''} onChange={e=>setWeightForm(f=>({...f,supplementKgDay:e.target.value}))} /></Field><Field label="Observações" col="1/-1"><Textarea rows={3} value={weightForm.notes||''} onChange={e=>setWeightForm(f=>({...f,notes:e.target.value}))} /></Field><div style={{gridColumn:"1/-1",display:'flex',justifyContent:'flex-end',gap:10}}><Btn variant="ghost" onClick={()=>setWeightForm(null)}>Cancelar</Btn><Btn onClick={saveWeightEntry}>Salvar</Btn></div></div></div>}
          {!form?._new && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
            {(isEquineInlineFocusMode
              ? (equineFocusedId ? sortedEquines.filter(eq=>String(eq.id)===equineFocusedId) : [])
              : sortedEquines
            ).map(eq=>{
              const isEditingEquine = !!form && !form._new && String(form.id)===String(eq.id);
              const isDetailEquine = !!equineDetail && String(equineDetail.id)===String(eq.id);
              const currentStall = stalls.find(s=>String(s.equineId||"")===String(eq.id));
              const plan = nutritionByEquine.get(String(eq.id));
              const currentWeightEntry = latestWeightsByEquine.get(String(eq.id));
              const activityInfo = equineActivityById.get(String(eq.id)) || inferEquineActivityLevel(schedule, eq.id, activityRules);
              const eqWeights = buildWeightSeries(weights, nutritionByEquine, eq.id);
              return <React.Fragment key={eq.id}>
                <div
                  style={{background:T.card,border:`1px solid ${(isEditingEquine||isDetailEquine)?CC_COLORS["Haras"]:T.border}`,borderRadius:14,overflow:"hidden",transition:"all .2s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=CC_COLORS["Haras"];e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor=(isEditingEquine||isDetailEquine)?CC_COLORS["Haras"]:T.border;e.currentTarget.style.transform="translateY(0)";}}>
                  <div style={{height:140,background:`${CC_COLORS["Haras"]}18`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",position:"relative",cursor:"pointer"}} onClick={()=>{setEquineDetail(cur=>cur && String(cur.id)===String(eq.id)?null:eq);setDetailSection("");if(form && !form._new && String(form.id)===String(eq.id)) setForm(null);}}>
                    {eq.photo ? <img src={eq.photo} alt={eq.name} style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <HorseIcon size={68} />}
                    <div style={{position:"absolute",top:8,right:8}}><Badge color={eq.status==="Ativo"?T.green:T.muted}>{eq.status}</Badge></div>
                  </div>
                  <div style={{padding:"12px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                      <div style={{minWidth:0,flex:1,cursor:"pointer"}} onClick={()=>{setEquineDetail(cur=>cur && String(cur.id)===String(eq.id)?null:eq);setDetailSection("");if(form && !form._new && String(form.id)===String(eq.id)) setForm(null);}}>
                        <div style={{fontSize:10,fontWeight:800,color:T.accent,letterSpacing:.6,marginBottom:2}}>{displayHarasRecordNumber(eq)}</div>
                        <div style={{fontWeight:800,color:T.text,fontSize:16,marginBottom:4}}>{eq.name}</div>
                        <div style={{fontSize:12,color:CC_COLORS["Haras"],fontWeight:600,marginBottom:6}}>{eq.breed}</div>
                      </div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"flex-end"}}>
                        <Btn size="sm" onClick={()=>{setEquineDetail(cur=>cur && String(cur.id)===String(eq.id)?null:eq);setDetailSection("");}} style={{background:isDetailEquine?`${CC_COLORS["Haras"]}22`:CC_COLORS["Haras"],color:isDetailEquine?CC_COLORS["Haras"]:'#fff',border:`1px solid ${CC_COLORS["Haras"]}`}}> {isDetailEquine?"Fechar":"Detalhes"} </Btn>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:6}}>
                      <Badge color={eq.sex==="Macho"?T.accent:CC_COLORS["Haras"]}>{eq.sex==="Macho"?"♂":"♀"} {eq.sex}</Badge>
                      {eq.color&&<Badge color={T.muted}>{eq.color}</Badge>}
                      {eq.weight&&<Badge color={T.yellow}>⚖️ {eq.weight} kg</Badge>}
                      {eq.height&&<Badge color={T.muted}>📏 {eq.height}</Badge>}
                      {currentStall&&<Badge color={CC_COLORS['Haras']}>🏠 {currentStall.name}</Badge>}
                    </div>
                    {eq.registration&&<div style={{fontSize:11,color:T.muted}}>📋 Reg: {eq.registration}</div>}
                    {eq.trainer&&<div style={{fontSize:11,color:T.muted}}>👤 Treinador: {eq.trainer}</div>}
                    {(eq.diet||[]).length>0&&(<div style={{marginTop:6,fontSize:11,color:T.green,display:"inline-flex",alignItems:"center",gap:6}}><EquineRationBagIcon size={16} /> {eq.diet.slice(0,3).join(", ")}{eq.diet.length>3?` +${eq.diet.length-3}`:""}</div>)}
                    {(()=>{ const feedTimes=(plan?.feedTimes||[]).length ? plan.feedTimes : HARAS_FEED_TIMES; const forageTimes=(plan?.forageTimes||[]).length ? plan.forageTimes : HARAS_HAY_TIMES; const feedPer = feedTimes.length ? round(num(plan?.feedQty,0)/feedTimes.length,2) : num(plan?.feedQty,0); const hayPer = forageTimes.length ? round(num(plan?.forageQty,0)/forageTimes.length,2) : num(plan?.forageQty,0); return <div style={{marginTop:8,display:'grid',gap:6}}>{num(plan?.feedQty,0)>0&&<div style={{fontSize:11,color:T.green,display:'inline-flex',alignItems:'center',gap:6}}><EquineRationBagIcon size={16} /> Ração: {feedPer} kg × {feedTimes.length||1}x/dia</div>}{num(plan?.forageQty,0)>0&&<div style={{fontSize:11,color:T.muted}}>🌿 Feno: {hayPer} kg × {forageTimes.length||1}x/dia</div>}<div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:4}}><Badge color={T.green}>Qtd. de Ração por Refeição: {feedPer} kg</Badge><Badge color={T.muted}>Qtd. de Feno por Refeição: {hayPer} kg</Badge></div></div>; })()}
                  </div>
                </div>
                {isEditingEquine && form && <div style={{gridColumn:"1/-1",marginTop:-6,background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>Editar Equino</div><Btn variant="ghost" onClick={()=>setForm(null)}>← Fechar</Btn></div>{renderEquineForm()}</div>}
                {isDetailEquine && equineDetail && <div style={{gridColumn:"1/-1",marginTop:-6,display:"flex",flexDirection:"column",gap:14,background:T.card,border:`1px solid ${CC_COLORS['Haras']}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}><div style={{display:"inline-flex",alignItems:"center",gap:8,fontWeight:800,color:T.text,fontSize:18}}><HorseIcon size={20} /> Ficha do Equino</div><Btn variant="ghost" onClick={()=>{setEquineDetail(null);setDetailSection("");}}>← Fechar</Btn></div>
                  <div style={{display:"flex",gap:14,alignItems:"center",padding:14,borderRadius:14,background:T.card2,border:`1px solid ${T.border}`}}>
                    <div style={{width:88,height:88,borderRadius:14,overflow:'hidden',background:`${CC_COLORS['Haras']}18`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{eq.photo?<img src={eq.photo} alt={eq.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<HorseIcon size={48} />}</div>
                    <div style={{flex:1}}><div style={{fontWeight:800,color:T.text,fontSize:20}}>{eq.name}</div><div style={{fontSize:13,color:CC_COLORS['Haras'],fontWeight:700}}>{eq.breed || 'Equino'}</div><div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8}}><Badge color={eq.status==='Ativo'?T.green:T.muted}>{eq.status}</Badge>{eq.sex&&<Badge color={T.accent}>{eq.sex}</Badge>}<Badge color={T.yellow}>⚖️ {round(num(currentWeightEntry?.weightKg || eq.weight, 0),1)} kg</Badge>{currentStall&&<Badge color={CC_COLORS['Haras']}>🏠 {currentStall.name}</Badge>}</div></div>
                    {canEdit && <Btn onClick={()=>{setEquineDetail(null);setDetailSection("");setForm({...eq,_new:false,stallId:currentStall?.id||''});}}>✏️ Editar</Btn>}
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:isCompactViewport()?'1fr':'repeat(2,minmax(0,1fr))',gap:12}}><DetailTile title="Cadastro" subtitle={`${eq.registration||'Sem registro'} · ${eq.owner||'Sem proprietário'}`} accent={CC_COLORS['Haras']} onClick={()=>setDetailSection('cadastro')} /><DetailTile title="Nutrição" subtitle={`${plan?.feedQty||'0'} kg/dia de ração · ${plan?.forageQty||'0'} kg/dia de feno`} accent={T.green} onClick={()=>setDetailSection('nutricao')} /><DetailTile title="Atividades" subtitle={`${activityInfo.levelName} · ${activityInfo.totalSessions} sessão(ões)/semana`} accent={T.accent} onClick={()=>setDetailSection('atividades')} /><DetailTile title="Pesagens" subtitle={`${eqWeights.length} registro(s) · peso atual ${round(num(currentWeightEntry?.weightKg || eq.weight, 0),1)} kg`} accent={T.yellow} onClick={()=>setDetailSection('pesagens')} /></div>
                  {detailSection==='cadastro' && <EquineCadastroDetail eq={eq} stall={currentStall} />}
                  {detailSection==='nutricao' && <EquineNutritionDetail eq={eq} plan={plan} rationItems={rationItems} supplementItems={supplementItems} hayItems={hayItems} setGuaranteeModal={setGuaranteeModal} />}
                  {detailSection==='atividades' && <EquineActivityDetail eq={eq} schedule={schedule} managementServices={managementServices} />}
                  {detailSection==='pesagens' && <EquineWeightsDetail eq={eq} points={eqWeights} onAdd={()=>{const latestPlan = nutritionByEquine.get(String(eq.id)); const activity = equineActivityById.get(String(eq.id)) || inferEquineActivityLevel(schedule, eq.id, activityRules); const currentWt = latestWeightsByEquine.get(String(eq.id)); setWeightForm({_new:true,equineId:eq.id,date:new Date().toISOString().slice(0,10),weightKg:currentWt?.weightKg || eq.weight || '',activityLevel:activity.levelName,feedKgDay:latestPlan?.feedQty || '',forageKgDay:latestPlan?.forageQty || '',supplementKgDay:latestPlan?.supplementQty || '',supplementLabel:(supplementItems.find(item=>String(item.id)===String(latestPlan?.supplementStockId))?.name)||'',feedLabel:(rationItems.find(item=>String(item.id)===String(latestPlan?.feedStockId))?.name)||'',notes:''});}} />}
                </div>}
              </React.Fragment>;
            })}          </div>}

          {/* Equine Form Modal */}
          <Modal open={false && !!form} onClose={()=>setForm(null)} title={form?(form._new?"Novo Equino no Haras":"Editar Equino"):""} wide>
            {form&&<div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:14}}>

              {/* Photo upload */}
              <div style={{gridColumn:"1/-1",display:"flex",gap:16,alignItems:"flex-start"}}>
                <div style={{width:120,height:120,borderRadius:12,background:`${CC_COLORS["Haras"]}18`,border:`2px dashed ${CC_COLORS["Haras"]}44`,overflow:"hidden",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",position:"relative"}}
                  onClick={()=>document.getElementById("equine-photo-input").click()}>
                  {form.photo
                    ? <img src={form.photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                    : <HorseIcon size={52} />
                  }
                  <div style={{position:"absolute",bottom:4,right:4,background:CC_COLORS["Haras"],borderRadius:6,padding:"2px 6px",fontSize:10,color:"#fff"}}>📷</div>
                </div>
                <input id="equine-photo-input" type="file" accept="image/*" style={{display:"none"}}
                  onChange={e=>{
                    const f2=e.target.files[0]; if(!f2) return;
                    const r=new FileReader(); r.onload=ev=>setForm(f=>({...f,photo:ev.target.result})); r.readAsDataURL(f2);
                  }}/>
                <div style={{flex:1}}>
                  <Field label="Nome *"><Input value={form.name} placeholder="Nome do equino" onChange={e=>setForm(f=>({...f,name:e.target.value}))}/></Field>
                </div>
              </div>

              <Field label="Raça">
                <Select value={form.breed} onChange={e=>setForm(f=>({...f,breed:e.target.value}))}>
                  {activeBreeds.map(b=><option key={b}>{b}</option>)}
                </Select>
              </Field>
              <Field label="Registro / Passaporte">
                <Input value={form.registration||""} placeholder="Nº de registro" onChange={e=>setForm(f=>({...f,registration:e.target.value}))}/>
              </Field>
              <Field label="Sexo">
                <Select value={form.sex} onChange={e=>setForm(f=>({...f,sex:e.target.value}))}>
                  {["Macho","Fêmea","Castrado"].map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}>
                  {["Ativo","Em tratamento","Aposentado","Vendido","Óbito"].map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="Data de Nascimento">
                <Input type="date" value={form.birthDate||""} onChange={e=>setForm(f=>({...f,birthDate:e.target.value}))}/>
              </Field>
              <Field label="Pelagem / Cor">
                <Select value={form.color||""} onChange={e=>setForm(f=>({...f,color:e.target.value}))}>
                  <option value="">Selecione a pelagem...</option>
                  {activeCoats.map(c=>(
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </Select>
                {form.color&&(()=>{
                  const coat=activeCoats.find(c=>c.name===form.color);
                  return coat?(
                    <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
                      <div style={{width:20,height:20,borderRadius:4,background:coat.hex,border:`1px solid ${T.border}`}}/>
                      <span style={{fontSize:11,color:T.muted}}>{coat.hex}</span>
                    </div>
                  ):null;
                })()}
              </Field>
              <Field label="Peso (kg)">
                <Input type="number" value={form.weight||""} onChange={e=>setForm(f=>({...f,weight:e.target.value}))}/>
              </Field>
              <Field label="Altura (mãos/cm)">
                <Input value={form.height||""} placeholder="Ex: 15.2 mãos / 160 cm" onChange={e=>setForm(f=>({...f,height:e.target.value}))}/>
              </Field>
              <Field label="Origem / Criador">
                <Input value={form.origin||""} placeholder="Haras de origem..." onChange={e=>setForm(f=>({...f,origin:e.target.value}))}/>
              </Field>
              <Field label="Proprietário">
                <>
                  <Input list="haras-owner-people" value={form.owner||""} onChange={e=>setForm(f=>({...f,owner:e.target.value}))}/>
                  <datalist id="haras-owner-people">{ownerPeople.map(person=><option key={person.id} value={person.name}>{person.tradeName?`${person.tradeName} · proprietário`:"Proprietário"}</option>)}</datalist>
                </>
              </Field>
              <Field label="Treinador" col="1/-1">
                <Input value={form.trainer||""} onChange={e=>setForm(f=>({...f,trainer:e.target.value}))}/>
              </Field>

              {/* Baia */}
              <Field label="🏠 Baia" col="1/-1">
                <Select value={form.stallId||""} onChange={e=>setForm(f=>({...f,stallId:e.target.value}))}>
                  <option value="">Sem baia definida</option>
                  {sortedStalls.map(s=>{
                    const ocupante=equines.find(eq=>eq.id===s.equineId&&eq.id!==form.id);
                    return(
                      <option key={s.id} value={s.id} disabled={!!ocupante}>
                        {s.name}{s.sector?` — ${s.sector}`:""}{ocupante?` (ocupada por ${ocupante.name})`:""}
                      </option>
                    );
                  })}
                </Select>
                {stalls.length===0&&<div style={{fontSize:11,color:T.yellow,marginTop:4}}>⚠️ Nenhuma baia cadastrada ainda. Crie baias na aba Baias.</div>}
              </Field>

              {/* Dieta individual */}
              <div style={{gridColumn:"1/-1",borderTop:`1px solid ${T.border}`,paddingTop:14}}>
                <div style={{fontWeight:700,color:T.green,fontSize:13,marginBottom:10}}><span style={{display:"inline-flex",alignItems:"center",gap:6}}><EquineRationBagIcon size={19} /> Dieta Individual</span></div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
                  {DIET_ITEMS.map(item=>{
                    const sel=(form.diet||[]).includes(item);
                    return(
                      <div key={item}
                        onClick={()=>setForm(f=>({...f,diet:sel?(f.diet||[]).filter(d=>d!==item):[...(f.diet||[]),item]}))}
                        style={{padding:"5px 12px",borderRadius:20,cursor:"pointer",fontSize:12,fontWeight:sel?700:400,transition:"all .15s",
                          background:sel?`${T.green}22`:T.card2,
                          border:`1px solid ${sel?T.green:T.border}`,
                          color:sel?T.green:T.muted}}>
                        {item}
                      </div>
                    );
                  })}
                </div>
                <Field label="Observações sobre a dieta" col="1/-1">
                  <Textarea rows={2} value={form.dietNotes||""} placeholder="Quantidades, horários, restrições alimentares..."
                    onChange={e=>setForm(f=>({...f,dietNotes:e.target.value}))}/>
                </Field>
              </div>

              <Field label="Observações Gerais" col="1/-1">
                <Textarea rows={2} value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
              </Field>

              <div style={{gridColumn:"1/-1",display:"flex",gap:10,justifyContent:"space-between"}}>
                <div>{!form._new&&canEdit&&<Btn variant="danger" onClick={()=>{setEquines(p=>dedupeById((p||[]).map(e=>e.id===form.id?makeHarasUpdatePending({...e,_deleted:true,deletedAt:new Date().toISOString(),...makeUpdateMeta(myName)}, "delete"):e)));setForm(null);show("🗑️ Removido.");syncOnSave("harasEquines");}}>🗑️ Excluir</Btn>}</div>
                <div style={{display:"flex",gap:10}}>
                  <Btn variant="ghost" onClick={()=>setForm(null)}>Cancelar</Btn>
                  {canEdit&&<Btn onClick={saveEquine}>Salvar</Btn>}
                </div>
              </div>
            </div>}
          </Modal>

          <Modal open={false && !!equineDetail} onClose={()=>{setEquineDetail(null);setDetailSection("");}} title={<span style={{display:"inline-flex",alignItems:"center",gap:8}}><HorseIcon size={20} /> Ficha do Equino</span>} wide>
            {equineDetail && (()=>{
              const eq = equineDetail;
              const currentStall = stalls.find(s=>String(s.equineId||"")===String(eq.id));
              const plan = nutritionByEquine.get(String(eq.id));
              const currentWeightEntry = latestWeightsByEquine.get(String(eq.id));
              const activityInfo = equineActivityById.get(String(eq.id)) || inferEquineActivityLevel(schedule, eq.id, activityRules);
              const eqWeights = buildWeightSeries(weights, nutritionByEquine, eq.id);
              return <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{display:"flex",gap:14,alignItems:"center",padding:14,borderRadius:14,background:T.card2,border:`1px solid ${T.border}`}}>
                  <div style={{width:88,height:88,borderRadius:14,overflow:'hidden',background:`${CC_COLORS['Haras']}18`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{eq.photo?<img src={eq.photo} alt={eq.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<HorseIcon size={48} />}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:800,color:T.text,fontSize:20}}>{eq.name}</div>
                    <div style={{fontSize:13,color:CC_COLORS['Haras'],fontWeight:700}}>{eq.breed || 'Equino'}</div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:8}}>
                      <Badge color={eq.status==='Ativo'?T.green:T.muted}>{eq.status}</Badge>
                      {eq.sex&&<Badge color={T.accent}>{eq.sex}</Badge>}
                      <Badge color={T.yellow}>⚖️ {round(num(currentWeightEntry?.weightKg || eq.weight, 0),1)} kg</Badge>
                      {currentStall&&<Badge color={CC_COLORS['Haras']}>🏠 {currentStall.name}</Badge>}
                    </div>
                  </div>
                  {canEdit && <Btn onClick={()=>{ const curStall = stalls.find(s=>String(s.equineId||'')===String(eq.id)); setForm({...eq,_new:false,stallId:curStall?.id||''}); }}>✏️ Editar</Btn>}
                </div>
                <div style={{display:'grid',gridTemplateColumns:isCompactViewport()?'1fr':'repeat(2,minmax(0,1fr))',gap:12}}>
                  <DetailTile title="Cadastro" subtitle={`${eq.registration||'Sem registro'} · ${eq.owner||'Sem proprietário'}`} accent={CC_COLORS['Haras']} onClick={()=>setDetailSection('cadastro')} />
                  <DetailTile title="Nutrição" subtitle={`${plan?.feedQty||'0'} kg/dia de ração · ${plan?.forageQty||'0'} kg/dia de feno`} accent={T.green} onClick={()=>setDetailSection('nutricao')} />
                  <DetailTile title="Atividades" subtitle={`${activityInfo.levelName} · ${activityInfo.totalSessions} sessão(ões)/semana`} accent={T.accent} onClick={()=>setDetailSection('atividades')} />
                  <DetailTile title="Pesagens" subtitle={`${eqWeights.length} registro(s) · peso atual ${round(num(currentWeightEntry?.weightKg || eq.weight, 0),1)} kg`} accent={T.yellow} onClick={()=>setDetailSection('pesagens')} />
                </div>
                {detailSection==='cadastro' && <EquineCadastroDetail eq={eq} stall={currentStall} />}
                {detailSection==='nutricao' && <EquineNutritionDetail eq={eq} plan={plan} rationItems={rationItems} supplementItems={supplementItems} hayItems={hayItems} setGuaranteeModal={setGuaranteeModal} />}
                {detailSection==='atividades' && <EquineActivityDetail eq={eq} schedule={schedule} managementServices={managementServices} />}
                {detailSection==='pesagens' && <EquineWeightsDetail eq={eq} points={eqWeights} onAdd={()=>{
                  const latestPlan = nutritionByEquine.get(String(eq.id));
                  const activity = equineActivityById.get(String(eq.id)) || inferEquineActivityLevel(schedule, eq.id, activityRules);
                  const currentWt = latestWeightsByEquine.get(String(eq.id));
                  setWeightForm({_new:true,equineId:eq.id,date:new Date().toISOString().slice(0,10),weightKg:currentWt?.weightKg || eq.weight || '',activityLevel:activity.levelName,feedKgDay:latestPlan?.feedQty || '',forageKgDay:latestPlan?.forageQty || '',supplementKgDay:latestPlan?.supplementQty || '',supplementLabel:(supplementItems.find(item=>String(item.id)===String(latestPlan?.supplementStockId))?.name)||'',feedLabel:(rationItems.find(item=>String(item.id)===String(latestPlan?.feedStockId))?.name)||'',notes:''});
                }} />}
              </div>;
            })()}
          </Modal>
        </>
      )}

      {/* ─── STALLS TAB ────────────────────────────────────────── */}
      {tab==="stalls"&&(
        <HarasStallsTab
          stalls={stalls} equines={equines} canEdit={canEdit} isCollab={isCollab}
          myName={myName} show={show}
          stallForm={stallForm} setStallForm={setStallForm}
          detailStall={detailStall} setDetailStall={setDetailStall}
          defStall={defStall} saveStall={saveStall} pastures={Array.isArray(pastures)?pastures:[]} removeStall={removeStall}
          submitInspection={submitInspection}
          requestRepair={requestRepair}
          reviewRepair={reviewRepair}
          displayHarasRecordNumber={displayHarasRecordNumber}
          onTransferEquine={(stallId, equineId) => {
            const transferMeta = makeUpdateMeta(myName);
            const updated = stalls.map(s => {
              const newEquineId = String(s.id)===String(stallId)
                ? equineId
                : (equineId && String(s.equineId)===String(equineId) ? "" : s.equineId);
              if(String(newEquineId) === String(s.equineId)) return s;
              return {...s, equineId: newEquineId, ...transferMeta};
            });
            setStalls(updated);
            // Refresh detailStall immediately
            if(detailStall){
              const refreshed = updated.find(s=>s.id===detailStall.id);
              if(refreshed) setDetailStall(refreshed);
            }
            // Push immediately to server so other devices get the change
            if(harasFarmId && navigator.onLine){
              (async()=>{
                try {
                  const key = `fz:${harasFarmId}:harasStalls`;
                  await DB.set(key, updated);
                  const locVer = (await DB.get(`${key}:_ver`)) || 0;
                  const res = await pushCollection(harasFarmId, "harasStalls", updated, locVer, myName);
                  if(res?.version) await DB.set(`${key}:_ver`, res.version);
                  window.dispatchEvent(new CustomEvent("fz:sync:updated",{detail:{key}}));
                } catch(e){ syncOnSave("harasStalls"); }
              })();
            } else {
              syncOnSave("harasStalls");
            }
            show("✅ Animal transferido!");
          }}
        />
      )}

      {/* ─── NUTRITION TAB ─────────────────────────────────────── */}
      {tab==="nutrition"&&(
        <>
          {nutritionForm&&<div ref={nutritionFormRef} style={{marginBottom:16,background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
              <div style={{fontWeight:800,color:T.text,fontSize:18}}>{nutritionForm._new?"Nova Configuração de Nutrição":"Editar Nutrição"}</div>
              <Btn variant="ghost" onClick={()=>setNutritionForm(null)}>← Fechar</Btn>
            </div>
            {(() => {
              const eq = equines.find(item=>String(item.id)===String(nutritionForm.equineId));
              const activityInfo = inferEquineActivityLevel(schedule, nutritionForm.equineId, activityRules);
              const breedRule = getBreedRule(breedRules, eq?.breed);
              const recommendation = suggestNutritionByEquine(eq, activityInfo, breedRule);
              const hayRecommendation = suggestHayByEquine(eq, activityInfo, breedRule, nutritionForm.hayQuality || "Boa");
              const liveWeight = num(nutritionForm.liveWeightKg || latestWeightsByEquine.get(String(nutritionForm.equineId))?.weightKg || eq?.weight, 0);
              const autoFeedKg = liveWeight ? round((num(nutritionForm.feedPctBW || recommendation.suggestedPctBW,0) * liveWeight) / 100, 2) : "";
              return <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}>
                <Field label="Equino"><Select value={nutritionForm.equineId||""} onChange={e=>setNutritionForm(f=>({...f,equineId:e.target.value}))}><option value="">Selecione...</option>{sortedEquines.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field>
                <Field label="Peso vivo usado no cálculo (kg)"><Input type="number" step="0.1" value={nutritionForm.liveWeightKg||liveWeight||''} onChange={e=>setNutritionForm(f=>({...f,liveWeightKg:e.target.value}))} /></Field>
                <Field label="Ração"><Select value={nutritionForm.feedStockId||""} onChange={e=>setNutritionForm(f=>({...f,feedStockId:e.target.value}))}><option value="">Selecione a ração...</option>{rationItems.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field>
                <Field label="Quantidade de Ração (kg/dia)"><Input type="number" step="0.001" value={nutritionForm.feedQty||autoFeedKg||""} onChange={e=>setNutritionForm(f=>({...f,feedQty:e.target.value}))} /></Field>
                <Field label="Feno do estoque"><Select value={nutritionForm.hayStockId||""} onChange={e=>setNutritionForm(f=>({...f,hayStockId:e.target.value}))}><option value="">Selecione o feno...</option>{hayItems.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field>
                <Field label="Quantidade de Feno (kg/dia)"><Input type="number" step="0.001" value={nutritionForm.forageQty||""} onChange={e=>setNutritionForm(f=>({...f,forageQty:e.target.value}))} /></Field>
                <Field label="Suplementação"><Select value={nutritionForm.supplementStockId||""} onChange={e=>setNutritionForm(f=>({...f,supplementStockId:e.target.value}))}><option value="">Selecione a suplementação...</option>{supplementItems.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field>
                <Field label="Quantidade de Suplementação (kg/dia)"><Input type="number" step="0.001" value={nutritionForm.supplementQty||""} onChange={e=>setNutritionForm(f=>({...f,supplementQty:e.target.value}))} /></Field>
                <Field label="Observações" col="1/-1"><Textarea rows={3} value={nutritionForm.notes||""} onChange={e=>setNutritionForm(f=>({...f,notes:e.target.value}))} /></Field>
                <div style={{gridColumn:"1/-1",display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}><div style={{fontSize:12,color:T.muted}}>Faixa sugerida: {recommendation.minPctBW.toFixed(2)}% a {recommendation.maxPctBW.toFixed(2)}% PV · feno {hayRecommendation.suggestedPctBW.toFixed(2)}% PV</div><div style={{display:"flex",gap:10}}><Btn variant="ghost" onClick={()=>setNutritionForm(null)}>Cancelar</Btn><Btn onClick={saveNutritionPlan}>Salvar</Btn></div></div>
              </div>;
            })()}
          </div>}
          {guaranteeModal&&<div style={{marginBottom:16,background:T.card,border:`1px solid ${T.border}`,borderRadius:18,padding:16}}><div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>📋 {guaranteeModal.name}</div><Btn variant="ghost" onClick={()=>setGuaranteeModal(null)}>← Fechar</Btn></div><div style={{fontSize:13,color:T.text,whiteSpace:"pre-wrap"}}>{guaranteeModal.guaranteeLevels || guaranteeModal.hayGuaranteeLevels || "Sem níveis de garantia cadastrados."}</div></div>}
        <HarasNutritionTab
          equines={isNutritionInlineFocusMode && nutritionFocusedEquineId ? equines.filter(eq=>String(eq.id)===nutritionFocusedEquineId) : equines}
          nutritionByEquine={nutritionByEquine}
          rationItems={rationItems}
          supplementItems={supplementItems}
          hayItems={hayItems}
          canEdit={canEdit}
          setNutritionForm={setNutritionForm}
          setGuaranteeModal={setGuaranteeModal}
          defaultFeedTimes={SL.HARAS_FEED_TIMES||HARAS_FEED_TIMES}
          activityRules={activityRules}
          breedRules={breedRules}
          schedule={schedule}
          latestWeightsByEquine={latestWeightsByEquine}
        />
        </>
      )}

      {/* ─── AGENDA TAB ────────────────────────────────────────── */}
      {tab==="agenda"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {agendaTab==="horarios" && feedTimesForm && <div ref={feedTimesFormRef} style={{background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>🕒 Horários de Alimentação</div><Btn variant="ghost" onClick={()=>setFeedTimesForm(null)}>← Fechar</Btn></div><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}><Field label="Equino"><Select value={feedTimesForm.equineId||''} onChange={e=>setFeedTimesForm(f=>({...f,equineId:e.target.value}))}>{sortedEquines.map(eq=><option key={eq.id} value={eq.id}>{eq.name}</option>)}</Select></Field><Field label="Modo dos horários"><Select value={feedTimesForm.mealMode||'general'} onChange={e=>setFeedTimesForm(f=>({...f,mealMode:e.target.value}))}><option value="general">Usar horários gerais</option><option value="individual">Definir horários individuais</option></Select></Field><Field label="Horários da ração" col="1/-1"><div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{(SL.HARAS_FEED_TIMES||HARAS_FEED_TIMES).map(hour=>{ const selected=(feedTimesForm.feedTimes||[]).includes(hour); return <button type="button" key={hour} onClick={()=>setFeedTimesForm(f=>({...f,feedTimes:selected?(f.feedTimes||[]).filter(t=>t!==hour):[...(f.feedTimes||[]),hour]}))} style={{padding:"6px 10px",borderRadius:999,border:`1px solid ${selected?T.green:T.border}`,background:selected?`${T.green}18`:T.card2,color:selected?T.green:T.text}}>{hour}</button>; })}</div></Field><Field label="Horários do feno" col="1/-1"><div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{HARAS_HAY_TIMES.map(hour=>{ const selected=(feedTimesForm.forageTimes||[]).includes(hour); return <button type="button" key={hour} onClick={()=>setFeedTimesForm(f=>({...f,forageTimes:selected?(f.forageTimes||[]).filter(t=>t!==hour):[...(f.forageTimes||[]),hour]}))} style={{padding:"6px 10px",borderRadius:999,border:`1px solid ${selected?T.yellow:T.border}`,background:selected?`${T.yellow}18`:T.card2,color:selected?T.yellow:T.text}}>{hour}</button>; })}</div></Field><div style={{gridColumn:"1/-1",display:"flex",justifyContent:"flex-end",gap:10}}><Btn variant="ghost" onClick={()=>setFeedTimesForm(null)}>Cancelar</Btn><Btn onClick={saveFeedTimesPlan}>Salvar</Btn></div></div></div>}
          {agendaTab==="manejos" && managementForm && <div ref={managementFormRef} style={{background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>{managementForm._new?"Novo Manejo":"Editar Manejo"}</div><Btn variant="ghost" onClick={()=>setManagementForm(null)}>← Fechar</Btn></div><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}><Field label="Equino"><Select value={managementForm.equineId||""} onChange={e=>setManagementForm(f=>({...f,equineId:e.target.value}))}><option value="">Selecione...</option>{sortedEquines.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</Select></Field><Field label="Manejo"><Select value={managementForm.managementType||""} onChange={e=>setManagementForm(f=>({...f,managementType:e.target.value,title:e.target.value}))}><option value="">Selecione...</option>{HARAS_MANAGEMENT_TYPES.map(opt=><option key={opt}>{opt}</option>)}</Select></Field><Field label="Data"><Input type="date" value={managementForm.plannedDate||""} onChange={e=>setManagementForm(f=>({...f,plannedDate:e.target.value,date:e.target.value}))}/></Field><Field label="Status"><Select value={managementForm.status||"Aberto"} onChange={e=>setManagementForm(f=>({...f,status:e.target.value}))}>{["Aberto","Em Execução","Pausada","Concluído","Cancelado"].map(opt=><option key={opt}>{opt}</option>)}</Select></Field><Field label="Horários" col="1/-1"><div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{(SL.HARAS_FEED_TIMES||HARAS_FEED_TIMES).map(hour=>{ const selected=(managementForm.plannedTimes||[]).includes(hour); return <button type="button" key={hour} onClick={()=>setManagementForm(f=>({...f,plannedTimes:selected?(f.plannedTimes||[]).filter(t=>t!==hour):[...(f.plannedTimes||[]),hour]}))} style={{padding:"6px 10px",borderRadius:999,border:`1px solid ${selected?T.yellow:T.border}`,background:selected?`${T.yellow}18`:T.card2,color:selected?T.yellow:T.text}}>{hour}</button>; })}</div></Field><Field label="Observações" col="1/-1"><Textarea rows={3} value={managementForm.notes||""} onChange={e=>setManagementForm(f=>({...f,notes:e.target.value,description:e.target.value}))}/></Field><div style={{gridColumn:"1/-1",display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}><div>{!managementForm._new&&<Btn variant="danger" onClick={()=>{setServices(prev=>dedupeById((prev||[]).map(item=>String(item.id)===String(managementForm.id)?markOperationalUpdatePending({...item,_deleted:true,deletedAt:new Date().toISOString(),...makeUpdateMeta(myName)}, { userName: myName || "", operationType:"delete" }):item))); setManagementForm(null); syncOnSave("services"); show("🗑️ Manejo removido.");}}>🗑️ Excluir</Btn>}</div><div style={{display:"flex",gap:10}}><Btn variant="ghost" onClick={()=>setManagementForm(null)}>Cancelar</Btn><Btn onClick={saveManagementItem}>Salvar</Btn></div></div></div></div>}
          {agendaTab==="treinos" && schedForm && <div ref={scheduleFormRef} style={{background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>{schedForm._new?"Agendar Treino":"Editar Treino"}</div><Btn variant="ghost" onClick={()=>setSchedForm(null)}>← Fechar</Btn></div><div style={{display:"flex",flexDirection:"column",gap:14}}><Field label="Equino *"><Select value={schedForm.equineId} onChange={e=>setSchedForm(f=>({...f,equineId:e.target.value}))}><option value="">Selecione...</option>{sortedEquines.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}</Select></Field><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:12}}><Field label="Dia da Semana"><Select value={schedForm.day} onChange={e=>setSchedForm(f=>({...f,day:Number(e.target.value)}))}>{WEEK_DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}</Select></Field><Field label="Horário"><Input type="time" value={schedForm.hour} onChange={e=>setSchedForm(f=>({...f,hour:e.target.value}))}/></Field><Field label="Tipo de Atividade"><Select value={schedForm.type} onChange={e=>setSchedForm(f=>({...f,type:e.target.value}))}>{(SL.TRAINING_TYPES||TRAINING_TYPES).map(t=><option key={t}>{t}</option>)}</Select></Field><Field label="Duração (min)"><Input type="number" min="15" step="15" value={schedForm.duration||60} onChange={e=>setSchedForm(f=>({...f,duration:Number(e.target.value)}))}/></Field></div><Field label="Observações"><Input value={schedForm.notes||""} placeholder="Instruções específicas..." onChange={e=>setSchedForm(f=>({...f,notes:e.target.value}))}/></Field><div style={{display:"flex",gap:10,justifyContent:"space-between"}}><div>{!schedForm._new&&<Btn variant="danger" onClick={()=>{setSchedule(p=>dedupeById((p||[]).filter(s=>s.id!==schedForm.id)));setSchedForm(null);show("🗑️ Removido.");syncOnSave("harasSchedule");}}>🗑️ Excluir</Btn>}</div><div style={{display:"flex",gap:10}}><Btn variant="ghost" onClick={()=>setSchedForm(null)}>Cancelar</Btn><Btn onClick={saveScheduleItem}>Salvar</Btn></div></div></div></div>}
            {agendaTab==="horarios"&&(
            <HarasFeedScheduleTab
              equines={equines}
              nutritionByEquine={nutritionByEquine}
              canEdit={canEdit}
              defaultFeedTimes={SL.HARAS_FEED_TIMES||HARAS_FEED_TIMES}
              setFeedTimesForm={setFeedTimesForm}
            />
          )}
          {agendaTab==="manejos"&&(
            <HarasManagementTab
              managementServices={managementServices}
              equines={equines}
              canEdit={canEdit}
              setManagementForm={setManagementForm}
              setServices={setServices}
              syncOnSave={syncOnSave}
              show={show}
            />
          )}
          {agendaTab==="treinos"&&(
            <HarasScheduleTab
              schedule={schedule} equines={equines} canEdit={canEdit}
              schedForm={schedForm} setSchedForm={setSchedForm}
              saveScheduleItem={saveScheduleItem}
              setSchedule={setSchedule} syncOnSave={syncOnSave} show={show}
              myName={myName} isCollab={isCollab} managementServices={managementServices}
            />
          )}
        </div>
      )}
      
    </div>
  );
}

// ── Haras Stalls Tab ──────────────────────────────────────────────────────────
function HarasStallsTab({ stalls, equines, canEdit, isCollab, myName, show,
  stallForm, setStallForm, detailStall, setDetailStall, defStall, saveStall, pastures, removeStall,
  submitInspection, requestRepair, reviewRepair, displayHarasRecordNumber, onTransferEquine }) {

  const [inspForm, setInspForm] = useState(null);
  const stallFocusedId = String((stallForm && !stallForm._new ? stallForm.id : "") || detailStall?.id || "");
  const isStallInlineFocusMode = !!(stallForm || detailStall || inspForm);

  const renderStallEditor = () => stallForm ? <div style={{marginTop:16,background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}>{!stallForm._new && <div style={{fontSize:12,fontWeight:800,color:T.accent,letterSpacing:.7,marginBottom:8}}>{displayHarasRecordNumber(stallForm)}</div>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
          <div style={{fontWeight:800,color:T.text,fontSize:18}}>{stallForm._new?"Nova Baia":"Editar Baia"}</div>
          <Btn variant="ghost" onClick={()=>setStallForm(null)}>← Fechar</Btn>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"140px 1fr",gap:12}}>
            <Field label="Prefixo">
              <Input value="Baia" disabled />
            </Field>
            <Field label="Identificação *">
              <Input value={stallForm.suffix||""} placeholder="01" onChange={e=>setStallForm(f=>({...f,suffix:e.target.value.replace(/[^0-9A-Za-z_-]/g,"")}))}/>
            </Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}>
            <Field label="Setor / Ala"><Input value={stallForm.sector||""} onChange={e=>setStallForm(f=>({...f,sector:e.target.value}))}/></Field>
            <Field label="Pasto vinculado"><Select value={stallForm.linkedPastureId||""} onChange={e=>setStallForm(f=>({...f,linkedPastureId:e.target.value||"",linkedPastureName: (pastures.find(p=>String(p.id)===String(e.target.value))?.name)||""}))}><option value="">Nenhum</option>{pastures.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</Select></Field>
            <Field label="Status"><Select value={stallForm.status||"Disponível"} onChange={e=>setStallForm(f=>({...f,status:e.target.value}))}>{Object.keys(BAIA_STATUS).map(s=><option key={s}>{s}</option>)}</Select></Field>
            <Field label="Animal na baia"><Select value={stallForm.equineId||""} onChange={e=>setStallForm(f=>({...f,equineId:e.target.value||"",equineName:(equines.find(eq=>String(eq.id)===String(e.target.value))?.name)||""}))}><option value="">Nenhum</option>{equines.filter(eq=>eq.status==="Ativo").map(eq=><option key={eq.id} value={eq.id}>{eq.name}</option>)}</Select></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"repeat(2,minmax(0,1fr))",gap:12}}>
            <Field label="💧 Aguada"><Select value={stallForm.water||"Limpa"} onChange={e=>setStallForm(f=>({...f,water:e.target.value}))}>{WATER_STATUS.map(s=><option key={s}>{s}</option>)}</Select></Field>
            <Field label="🪣 Cocho"><Select value={stallForm.trough||"Em Conformidade"} onChange={e=>setStallForm(f=>({...f,trough:e.target.value}))}>{TROUGH_STATUS.map(s=><option key={s}>{s}</option>)}</Select></Field>
            <Field label="🛏️ Cama (espessura)"><Select value={stallForm.bedThickness||"Em Conformidade"} onChange={e=>setStallForm(f=>({...f,bedThickness:e.target.value}))}>{BED_THICKNESS.map(s=><option key={s}>{s}</option>)}</Select></Field>
            <Field label="💦 Cama (umidade)"><Select value={stallForm.bedMoisture||"Seca"} onChange={e=>setStallForm(f=>({...f,bedMoisture:e.target.value}))}>{BED_MOISTURE.map(s=><option key={s}>{s}</option>)}</Select></Field>
          </div>
          <Field label="Observações"><Textarea rows={3} value={stallForm.notes||""} onChange={e=>setStallForm(f=>({...f,notes:e.target.value}))}/></Field>
          <div style={{display:"flex",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
            <div>{!stallForm._new&&<Btn variant="danger" onClick={()=>removeStall(stallForm)}>🗑️ Excluir</Btn>}</div>
            <div style={{display:"flex",gap:10}}><Btn variant="ghost" onClick={()=>setStallForm(null)}>Cancelar</Btn><Btn onClick={saveStall}>Salvar</Btn></div>
          </div>
        </div>
      </div> : null;

  return(
    <div>
      {canEdit&&<div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
        <Btn onClick={()=>setStallForm({...defStall})}>+ Nova Baia</Btn>
      </div>}

      {stallForm?._new && renderStallEditor()}

      {stalls.length===0&&(
        <div style={{color:T.muted,textAlign:"center",padding:48}}>Nenhuma baia cadastrada.</div>
      )}

      {/* Stalls grid */}
      {!stallForm?._new && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:12}}>
        {(isStallInlineFocusMode
          ? (stallFocusedId ? stalls.filter(st=>String(st.id)===stallFocusedId) : [])
          : stalls
        ).map(st=>{
          const cfg   = BAIA_STATUS[st.status]||{color:T.muted,icon:"❓"};
          const eq    = equines.find(e=>st.equineId && String(e.id)===String(st.equineId));
          const pRep  = (st.repairs||[]).filter(r=>r.status==="Pendente").length;
          const isDetailOpen = detailStall && String(st.id)===String(detailStall.id);
          const isEditingStall = stallForm && !stallForm._new && String(st.id)===String(stallForm.id);
          return(
            <React.Fragment key={st.id}>
            <div
              onClick={()=>setDetailStall(st)}
              style={{background:T.card,border:`2px solid ${cfg.color}44`,borderRadius:14,overflow:"hidden",cursor:"pointer",transition:"all .2s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=cfg.color;e.currentTarget.style.transform="translateY(-2px)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=`${cfg.color}44`;e.currentTarget.style.transform="translateY(0)";}}>

              {/* Header */}
              <div style={{background:`${cfg.color}18`,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontWeight:700,color:T.text,fontSize:14}}>{st.name}</div>
                  {st.sector&&<div style={{fontSize:11,color:T.muted}}>{st.sector}</div>}
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                  <span style={{background:`${cfg.color}22`,color:cfg.color,borderRadius:8,padding:"2px 8px",fontSize:12,fontWeight:700}}>
                    {cfg.icon} {st.status}
                  </span>
                  {pRep>0&&<span style={{background:`${T.red}22`,color:T.red,borderRadius:8,padding:"1px 6px",fontSize:10,fontWeight:700}}>🔧 {pRep} reparo(s)</span>}
                </div>
              </div>

              {/* Animal */}
              <div style={{padding:"8px 14px",borderBottom:`1px solid ${T.border}33`}}>
                {eq
                  ? <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {eq.photo?<img src={eq.photo} alt="" style={{width:28,height:28,borderRadius:6,objectFit:"cover"}}/>:<HorseIcon size={24} />}
                      <div><div style={{fontWeight:600,color:T.text,fontSize:13}}>{eq.name}</div><div style={{fontSize:10,color:T.muted}}>{eq.breed}</div></div>
                    </div>
                  : <div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>Baia vazia</div>
                }
              </div>

              {/* Quick status indicators */}
              <div style={{padding:"8px 14px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                {[
                  {label:"💧 Aguada", val:st.water,     good:["Limpa"],         bad:["Suja","Vazia"]},
                  {label:"🪣 Cocho",  val:st.trough,    good:["Em Conformidade"],bad:["Danificado"]},
                  {label:"🛏️ Cama",   val:st.bedThickness,good:["Em Conformidade"],bad:[]},
                  {label:"💦 Umidade",val:st.bedMoisture,good:["Seca"],          bad:["Molhada"]},
                ].map(({label,val,good,bad})=>(
                  <div key={label} style={{fontSize:10}}>
                    <span style={{color:T.muted}}>{label}: </span>
                    <span style={{fontWeight:600,color:val&&good.includes(val)?T.green:val&&bad.includes(val)?T.red:T.yellow}}>{val||"—"}</span>
                  </div>
                ))}
              </div>

              {st.lastInspection&&(
                <div style={{padding:"4px 14px 8px",fontSize:10,color:T.muted}}>
                  Vistoria: {new Date(st.lastInspection).toLocaleString("pt-BR")} · {st.lastInspector}
                </div>
              )}
            </div>
            {isDetailOpen && <div style={{gridColumn:"1/-1"}}>
              <StallDetailModal
                stall={detailStall} equines={equines} canEdit={canEdit} isCollab={isCollab} myName={myName}
                onClose={()=>setDetailStall(null)}
                onEdit={()=>{setStallForm({...detailStall,_new:false});setDetailStall(null);}}
                onInspect={()=>setInspForm({
                  status:detailStall.status, water:detailStall.water||"Limpa",
                  trough:detailStall.trough||"Em Conformidade",
                  bedThickness:detailStall.bedThickness||"Em Conformidade",
                  bedMoisture:detailStall.bedMoisture||"Seca",
                  notes:"", photo:"",
                })}
                inspForm={inspForm} setInspForm={setInspForm}
                onSubmitInspection={(data)=>{submitInspection(detailStall,data);setInspForm(null);}}
                onRequestRepair={(item,desc)=>requestRepair(detailStall,item,desc)}
                onReviewRepair={(repairId,status,note)=>reviewRepair(detailStall,repairId,status,note)}
                onTransferEquine={(equineId)=>onTransferEquine(detailStall.id,equineId)}
                allEquines={equines}
              />
            </div>}
            {isEditingStall && <div style={{gridColumn:"1/-1"}}>{renderStallEditor()}</div>}
            </React.Fragment>
          );
        })}
      </div>}
    </div>
  );
}

// ── Stall Detail Modal ────────────────────────────────────────────────────────
function StallDetailModal({ stall, equines, canEdit, isCollab, myName, onClose, onEdit,
  onInspect, inspForm, setInspForm, onSubmitInspection, onRequestRepair, onReviewRepair,
  onTransferEquine, allEquines }) {
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferTo,   setTransferTo]   = useState("");

  const [repairItem, setRepairItem] = useState("");
  const [repairDesc, setRepairDesc] = useState("");
  const [showRepairForm, setShowRepairForm] = useState(false);
  const [repairReviewForm, setRepairReviewForm] = useState(null);

  const cfg = BAIA_STATUS[stall.status]||{color:T.muted,icon:"❓"};
  const eq  = equines.find(e=>stall.equineId && String(e.id)===String(stall.equineId));

  const FieldRow = ({label, val, options, good, bad, onChange}) => {
    const c = val&&good?.includes(val)?T.green:val&&bad?.includes(val)?T.red:T.yellow;
    return(
      <div style={{display:"grid",gridTemplateColumns:"120px 1fr",alignItems:"center",gap:10,padding:"6px 0",borderBottom:`1px solid ${T.border}22`}}>
        <div style={{fontSize:12,color:T.muted}}>{label}</div>
        {onChange
          ? <Select value={val||""} onChange={e=>onChange(e.target.value)} style={{fontSize:12}}>
              {options.map(o=><option key={o}>{o}</option>)}
            </Select>
          : <span style={{fontWeight:600,color:c,fontSize:13}}>{val||"—"}</span>
        }
      </div>
    );
  };

  return(
    <div style={{marginTop:16,background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)",display:"flex",flexDirection:"column",gap:0}}>
      <div style={{display:"flex",flexDirection:"column",gap:0}}>

        {/* Header */}
        <div style={{background:`${cfg.color}18`,borderRadius:12,padding:"14px 18px",marginBottom:16,border:`1px solid ${cfg.color}33`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <div style={{fontWeight:800,color:T.text,fontSize:18}}>{stall.name}</div>
            {stall.sector&&<div style={{fontSize:12,color:T.muted,marginTop:2}}>{stall.sector}</div>}
            <div style={{marginTop:6}}><Badge color={cfg.color}>{cfg.icon} {stall.status}</Badge></div>
          </div>
          <div style={{display:"flex",gap:8}}>
            {canEdit&&<Btn size="sm" variant="ghost" onClick={onEdit}>✏️</Btn>}
            {canEdit&&<Btn size="sm" onClick={onInspect} style={{background:`${T.accent}22`,color:T.accent,border:`1px solid ${T.accent}44`}}>
              🔍 Registrar Vistoria
            </Btn>}
          </div>
        </div>

        {/* Equine */}
        {eq&&(
          <div style={{background:T.card2,borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
            {eq.photo?<img src={eq.photo} alt="" style={{width:44,height:44,borderRadius:8,objectFit:"cover"}}/>:<HorseIcon size={36} />}
            <div>
              <div style={{fontWeight:700,color:T.text}}>{eq.name}</div>
              <div style={{fontSize:12,color:CC_COLORS["Haras"]}}>{eq.breed}</div>
            </div>
          </div>
        )}

        {/* Transfer equine between stalls — admin/gestor and collaborator */}
        {(canEdit||isCollab)&&(
          <div style={{marginBottom:12}}>
            {!showTransfer
              ? <Btn size="sm" variant="ghost" onClick={()=>setShowTransfer(true)}>🔄 Transferir Animal de Baia</Btn>
              : <div style={{background:T.card2,borderRadius:10,padding:"12px 14px",border:`1px solid ${T.border}`,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <div style={{fontSize:13,fontWeight:600,color:T.text,flexShrink:0}}>Mover animal para esta baia:</div>
                  <Select value={transferTo} onChange={e=>setTransferTo(e.target.value)} style={{flex:1,minWidth:140}}>
                    <option value="">Selecione o animal...</option>
                    {(allEquines||[]).filter(e=>e.status==="Ativo"&&e.id!==stall.equineId).map(e=>(
                      <option key={e.id} value={e.id}>{e.name} — {e.breed}</option>
                    ))}
                    <option value="empty">🚪 Deixar baia vazia</option>
                  </Select>
                  <Btn size="sm" onClick={()=>{
                    if(!transferTo){return;}
                    // Convert to number if possible (same type as equine ids)
                    const eqId = transferTo==="empty" ? "" : (isNaN(Number(transferTo)) ? transferTo : Number(transferTo));
                    onTransferEquine(eqId);
                    setShowTransfer(false); setTransferTo("");
                  }}>Confirmar</Btn>
                  <Btn size="sm" variant="ghost" onClick={()=>setShowTransfer(false)}>Cancelar</Btn>
                </div>
            }
          </div>
        )}

        {/* Current status grid */}
        <div style={{background:T.card2,borderRadius:10,padding:"12px 16px",marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>📊 Estado Atual</div>
          <FieldRow label="💧 Aguada"      val={stall.water}          good={["Limpa"]}            bad={["Suja","Vazia"]}/>
          <FieldRow label="🪣 Cocho Ração"  val={stall.trough}         good={["Em Conformidade"]}  bad={["Danificado"]}/>
          <FieldRow label="🛏️ Cama (espessura)" val={stall.bedThickness} good={["Em Conformidade"]} bad={[]}/>
          <FieldRow label="💦 Cama (umidade)"  val={stall.bedMoisture}   good={["Seca"]}            bad={["Molhada"]}/>
          {stall.lastInspection&&(
            <div style={{fontSize:11,color:T.muted,marginTop:8}}>
              Última vistoria: {new Date(stall.lastInspection).toLocaleString("pt-BR")} · {stall.lastInspector}
            </div>
          )}
          {stall.inspectionPhoto&&(
            <img src={stall.inspectionPhoto} alt="" style={{marginTop:8,width:80,height:80,borderRadius:8,objectFit:"cover",cursor:"zoom-in"}}
              onClick={()=>window.open(stall.inspectionPhoto,"_blank")}/>
          )}
        </div>

        {/* Inspection form */}
        {inspForm&&(
          <div style={{background:`${T.accent}10`,border:`1px solid ${T.accent}33`,borderRadius:12,padding:"14px 16px",marginBottom:16}}>
            <div style={{fontWeight:700,color:T.accent,fontSize:13,marginBottom:12}}>🔍 Nova Vistoria</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <Field label="Status da Baia">
                <Select value={inspForm.status} onChange={e=>setInspForm(f=>({...f,status:e.target.value}))}>
                  {Object.keys(BAIA_STATUS).map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="💧 Aguada">
                <Select value={inspForm.water} onChange={e=>setInspForm(f=>({...f,water:e.target.value}))}>
                  {WATER_STATUS.map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="🪣 Cocho de Ração">
                <Select value={inspForm.trough} onChange={e=>setInspForm(f=>({...f,trough:e.target.value}))}>
                  {TROUGH_STATUS.map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="🛏️ Cama — Espessura">
                <Select value={inspForm.bedThickness} onChange={e=>setInspForm(f=>({...f,bedThickness:e.target.value}))}>
                  {BED_THICKNESS.map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="💦 Cama — Umidade">
                <Select value={inspForm.bedMoisture} onChange={e=>setInspForm(f=>({...f,bedMoisture:e.target.value}))}>
                  {BED_MOISTURE.map(s=><option key={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="📷 Foto da vistoria">
                <input type="file" accept="image/*" style={{fontSize:11,color:T.muted}}
                  onChange={e=>{const f2=e.target.files[0];if(!f2)return;const r=new FileReader();r.onload=ev=>setInspForm(f=>({...f,photo:ev.target.result}));r.readAsDataURL(f2);}}/>
              </Field>
              <Field label="Observações" col="1/-1">
                <Input value={inspForm.notes} placeholder="Observações gerais da vistoria..."
                  onChange={e=>setInspForm(f=>({...f,notes:e.target.value}))}/>
              </Field>
            </div>
            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <Btn size="sm" variant="ghost" onClick={()=>setInspForm(null)}>Cancelar</Btn>
              <Btn size="sm" onClick={()=>onSubmitInspection(inspForm)}>✅ Confirmar Vistoria</Btn>
            </div>
          </div>
        )}

        {/* Repair requests */}
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8}}>🔧 Pedidos de Reparo</div>
            <Btn size="sm" variant="ghost" onClick={()=>setShowRepairForm(!showRepairForm)}>+ Solicitar Reparo</Btn>
          </div>

          {showRepairForm&&(
            <div style={{background:T.card2,borderRadius:10,padding:"12px 14px",marginBottom:10,border:`1px solid ${T.border}`}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:8}}>
                <Field label="Item">
                  <Select value={repairItem} onChange={e=>setRepairItem(e.target.value)}>
                    <option value="">Selecione...</option>
                    {["Aguada","Cocho de Ração","Cama","Estrutura da Baia","Iluminação","Ventilação","Outro"].map(i=><option key={i}>{i}</option>)}
                  </Select>
                </Field>
                <Field label="Descrição">
                  <Input value={repairDesc} placeholder="Descreva o problema..." onChange={e=>setRepairDesc(e.target.value)}/>
                </Field>
              </div>
              <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
                <Btn size="sm" variant="ghost" onClick={()=>setShowRepairForm(false)}>Cancelar</Btn>
                <Btn size="sm" onClick={()=>{
                  if(!repairItem||!repairDesc.trim()){return;}
                  onRequestRepair(repairItem,repairDesc);
                  setRepairItem(""); setRepairDesc(""); setShowRepairForm(false);
                }}>Enviar Pedido</Btn>
              </div>
            </div>
          )}

          {(stall.repairs||[]).length===0&&<div style={{fontSize:12,color:T.muted,fontStyle:"italic"}}>Nenhum pedido de reparo.</div>}
          {(stall.repairs||[]).map(rep=>{
            const rc = REPAIR_STATUS[rep.status]||"#64748B";
            return(
              <div key={rep.id} style={{background:T.card2,borderRadius:10,padding:"10px 14px",marginBottom:8,border:`1px solid ${rc}33`}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,color:T.text,fontSize:13}}>🔧 {rep.item}</div>
                    <div style={{fontSize:12,color:T.muted,marginTop:2}}>{rep.description}</div>
                    <div style={{fontSize:10,color:T.muted,marginTop:4}}>Por {rep.requestedBy} · {new Date(rep.requestedAt).toLocaleString("pt-BR")}</div>
                    {rep.adminNote&&<div style={{fontSize:11,color:T.accent,marginTop:4,fontStyle:"italic"}}>Admin: "{rep.adminNote}"</div>}
                  </div>
                  <Badge color={rc}>{rep.status}</Badge>
                </div>
                {/* Admin review buttons */}
                {canEdit&&rep.status==="Pendente"&&(
                  <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
                    {["Aprovado","Em Análise","Negado"].map(statusOption=>(
                      <Btn key={statusOption} size="sm" variant="ghost"
                        style={{borderColor:REPAIR_STATUS[statusOption],color:REPAIR_STATUS[statusOption]}}
                        onClick={()=>setRepairReviewForm({repairId:rep.id,status:statusOption,note:""})}>
                        {statusOption==="Aprovado"?"✅":statusOption==="Em Análise"?"🔍":"❌"} {statusOption}
                      </Btn>
                    ))}
                  </div>
                )}
                {repairReviewForm && repairReviewForm.repairId===rep.id && (
                  <div style={{marginTop:10,background:`${T.accent}10`,border:`1px solid ${T.accent}33`,borderRadius:10,padding:12,display:"flex",flexDirection:"column",gap:10}}>
                    <div style={{fontSize:12,fontWeight:700,color:T.text}}>Revisão do pedido</div>
                    <Field label="Observação do administrador">
                      <Textarea rows={3} value={repairReviewForm.note||""} onChange={e=>setRepairReviewForm(f=>({...f,note:e.target.value}))} placeholder="Opcional" />
                    </Field>
                    <div style={{display:"flex",justifyContent:"flex-end",gap:8}}>
                      <Btn size="sm" variant="ghost" onClick={()=>setRepairReviewForm(null)}>Cancelar</Btn>
                      <Btn size="sm" onClick={()=>{onReviewRepair(rep.id, repairReviewForm.status, repairReviewForm.note||""); setRepairReviewForm(null);}}>Salvar revisão</Btn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Inspection history */}
        {(stall.history||[]).length>0&&(
          <div>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>📜 Histórico de Vistorias</div>
            {[...(stall.history||[])].reverse().slice(0,5).map((h,i)=>{
              const hcfg = BAIA_STATUS[h.status]||{color:T.muted,icon:"❓"};
              return(
                <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<4?`1px solid ${T.border}22`:"none"}}>
                  <span style={{fontSize:16,flexShrink:0}}>{hcfg.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:T.text}}>
                      {h.status} · 💧{h.water} · 🪣{h.trough} · 🛏️{h.bedThickness} · 💦{h.bedMoisture}
                    </div>
                    {h.notes&&<div style={{fontSize:11,color:T.muted,marginTop:1}}>{h.notes}</div>}
                    <div style={{fontSize:10,color:T.muted,marginTop:2}}>{h.inspector} · {new Date(h.ts).toLocaleString("pt-BR")}</div>
                  </div>
                  {h.photo&&<img src={h.photo} alt="" style={{width:48,height:48,borderRadius:6,objectFit:"cover",flexShrink:0,cursor:"zoom-in"}} onClick={()=>window.open(h.photo,"_blank")}/>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailTile({ title, subtitle, accent, onClick }){
  return <button type="button" onClick={onClick} style={{textAlign:'left',padding:14,borderRadius:14,border:`1px solid ${accent}44`,background:`${accent}12`,cursor:'pointer'}}>
    <div style={{fontSize:12,fontWeight:800,color:accent,marginBottom:6}}>{title}</div>
    <div style={{fontSize:13,color:T.text,fontWeight:700}}>{subtitle}</div>
    <div style={{fontSize:11,color:T.muted,marginTop:6}}>Toque para abrir os detalhes.</div>
  </button>;
}

function EquineCadastroDetail({ eq, stall }){
  const rows = [
    ['Registro', eq.registration || '—'],
    ['Nascimento', eq.birthDate || '—'],
    ['Pelagem', eq.color || '—'],
    ['Origem', eq.origin || '—'],
    ['Proprietário', eq.owner || '—'],
    ['Treinador', eq.trainer || '—'],
    ['Baia', stall?.name || 'Sem baia'],
    ['Observações', eq.notes || '—'],
  ];
  return <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}>{rows.map(([k,v])=><div key={k} style={{display:'grid',gridTemplateColumns:'140px 1fr',gap:10,padding:'8px 0',borderBottom:`1px solid ${T.border}22`}}><div style={{fontSize:12,color:T.muted}}>{k}</div><div style={{fontSize:13,color:T.text,fontWeight:600}}>{v}</div></div>)}</div>;
}
function EquineNutritionDetail({ eq, plan, rationItems, supplementItems, hayItems, setGuaranteeModal }){
  const feed = rationItems.find(item=>String(item.id)===String(plan?.feedStockId));
  const supp = supplementItems.find(item=>String(item.id)===String(plan?.supplementStockId));
  const hay = hayItems.find(item=>String(item.id)===String(plan?.hayStockId));
  return <div style={{display:'grid',gridTemplateColumns:isCompactViewport()?'1fr':'repeat(2,minmax(0,1fr))',gap:12}}>
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}>
      <div style={{fontSize:12,fontWeight:800,color:T.green,marginBottom:8}}>Ração</div>
      <div style={{fontWeight:800,color:T.text}}>{feed?.name || 'Não definida'}</div>
      <div style={{fontSize:13,color:T.muted,marginTop:6}}>{plan?.feedQty || 0} kg/dia · {plan?.feedPctBW || plan?.recommendedPctBW || '—'}% PV</div>
      <div style={{fontSize:12,color:T.muted,marginTop:6}}>Faixa sugerida: {plan?.recommendedPctRange || '—'}</div>
      {feed && <button type="button" onClick={()=>setGuaranteeModal(feed)} style={{marginTop:8,background:'none',border:'none',padding:0,color:T.accent,cursor:'pointer',fontSize:12,fontWeight:700}}>Ver níveis de garantia</button>}
    </div>
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}>
      <div style={{fontSize:12,fontWeight:800,color:T.yellow,marginBottom:8}}>Suplementação e dieta especial</div>
      <div style={{fontWeight:800,color:T.text}}>{supp?.name || 'Não definida'}</div>
      <div style={{fontSize:13,color:T.muted,marginTop:6}}>{plan?.supplementQty || 0} kg/dia · feno {plan?.forageQty || 0} kg/dia</div>
      {!!plan?.specialDiet && <div style={{marginTop:8,fontSize:12,color:T.red,fontWeight:700}}>Animal em dieta especial</div>}
      <div style={{fontSize:12,color:T.muted,marginTop:4}}>Horários do feno: {(plan?.forageTimes||[]).join(', ') || 'não definidos'}</div>{hay && <div style={{display:'flex',gap:8,alignItems:'center',marginTop:10,flexWrap:'wrap'}}><Badge color={T.muted}>Feno</Badge><div style={{fontSize:12,color:T.text,fontWeight:700}}>{hay.name}</div><div style={{fontSize:12,color:T.muted}}>Qualidade: {plan?.hayQuality || 'Boa'}</div><Btn size="sm" variant="ghost" onClick={()=>setGuaranteeModal({...hay, guaranteeLevels: hay.guaranteeLevels || hay.hayGuaranteeLevels || 'Sem níveis de garantia cadastrados.'})}>Níveis de garantia</Btn></div>}{!!(plan?.specialDietReasons||[]).length && <div style={{fontSize:12,color:T.muted,marginTop:6}}>{plan.specialDietReasons.join(', ')}</div>}
      {supp && <button type="button" onClick={()=>setGuaranteeModal(supp)} style={{marginTop:8,background:'none',border:'none',padding:0,color:T.accent,cursor:'pointer',fontSize:12,fontWeight:700}}>Ver níveis de garantia</button>}
    </div>
  </div>;
}
function EquineActivityDetail({ eq, schedule, managementServices }){
  const trainings = (schedule||[]).filter(item=>String(item?.equineId||'')===String(eq.id));
  const manejos = (managementServices||[]).filter(item=>String(item?.equineId||'')===String(eq.id));
  return <div style={{display:'grid',gridTemplateColumns:isCompactViewport()?'1fr':'repeat(2,minmax(0,1fr))',gap:12}}>
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}><div style={{fontWeight:800,color:T.text,marginBottom:8}}>Treinos</div>{trainings.length?trainings.slice(0,10).map(item=><div key={item.id} style={{fontSize:12,color:T.text,padding:'6px 0',borderBottom:`1px solid ${T.border}22`}}>{WEEK_DAYS[item.day]} · {item.hour} · {item.type}</div>):<div style={{fontSize:12,color:T.muted}}>Sem treinos cadastrados.</div>}</div>
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}><div style={{fontWeight:800,color:T.text,marginBottom:8}}>Manejos</div>{manejos.length?manejos.slice(0,10).map(item=><div key={item.id} style={{fontSize:12,color:T.text,padding:'6px 0',borderBottom:`1px solid ${T.border}22`}}>{item.plannedDate||item.date} · {item.harasManagementType||item.title}</div>):<div style={{fontSize:12,color:T.muted}}>Sem manejos vinculados.</div>}</div>
  </div>;
}
function EquineWeightsDetail({ eq, points, onAdd }){
  return <div style={{display:'flex',flexDirection:'column',gap:12}}>
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}>
      <div style={{display:'flex',justifyContent:'space-between',gap:10,alignItems:'center',marginBottom:8}}><div><div style={{fontWeight:800,color:T.text}}>Histórico de pesagens</div><div style={{fontSize:12,color:T.muted}}>Peso vivo relacionado a ração, feno e suplementação do período.</div></div><Btn size="sm" onClick={onAdd}>+ Nova pesagem</Btn></div>
      <MiniTrendChart points={points} />
    </div>
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}>
      {(points||[]).length ? points.slice().reverse().map(item=><div key={item.id} style={{padding:'10px 0',borderBottom:`1px solid ${T.border}22`}}><div style={{display:'flex',justifyContent:'space-between',gap:10}}><div style={{fontSize:13,fontWeight:700,color:T.text}}>{fmtDate(item.date || item.createdAt || "")}</div><div style={{fontSize:13,color:T.yellow,fontWeight:700}}>{item.weightKg} kg</div></div><div style={{fontSize:12,color:T.muted,marginTop:4}}>Atividade: {item.activityLevel || '—'} · Ração: {item.feedKgDay || 0} kg/dia · Feno: {item.forageKgDay || 0} kg/dia</div><div style={{fontSize:12,color:T.muted}}>Suplementação: {item.supplementKgDay || 0} kg/dia {item.supplementLabel?`· ${item.supplementLabel}`:''}</div>{item.notes&&<div style={{fontSize:12,color:T.text,marginTop:4}}>{item.notes}</div>}</div>) : <div style={{fontSize:12,color:T.muted}}>Nenhuma pesagem cadastrada.</div>}
    </div>
  </div>;
}

function HarasNutritionTab({ equines, nutritionByEquine, rationItems, supplementItems, hayItems, canEdit, setNutritionForm, setGuaranteeModal, defaultFeedTimes, activityRules, breedRules, schedule, latestWeightsByEquine }) {
  const sortedEquines = React.useMemo(()=> (equines||[]).slice().sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""),"pt-BR")), [equines]);
  return (
    <div>
      {canEdit && <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}><Btn onClick={()=>setNutritionForm({_new:true,equineId:"",feedStockId:"",feedQty:"",feedPctBW:"",hayStockId:"",forageQty:"",foragePctBW:"1.50",hayQuality:"Boa",forageMealMode:"general",forageTimes:[...HARAS_HAY_TIMES],supplementStockId:"",supplementQty:"",specialDiet:false,specialDietReasons:[],mealMode:"general",feedTimes:[...defaultFeedTimes],notes:""})}>+ Configurar Nutrição</Btn></div>}
      {equines.length===0 && <div style={{color:T.muted,textAlign:"center",padding:48}}>Cadastre equinos no Plantel para montar a nutrição.</div>}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
        {sortedEquines.map(eq=>{
          const plan = nutritionByEquine.get(String(eq.id));
          const feed = rationItems.find(item=>String(item.id)===String(plan?.feedStockId));
          const supp = supplementItems.find(item=>String(item.id)===String(plan?.supplementStockId));
          const hay = hayItems.find(item=>String(item.id)===String(plan?.hayStockId));
          const times = (plan?.feedTimes||[]).length ? plan.feedTimes : defaultFeedTimes;
          const forageTimes = (plan?.forageTimes||[]).length ? plan.forageTimes : HARAS_HAY_TIMES;
          const activityInfo = inferEquineActivityLevel(schedule, eq.id, activityRules);
          const breedRule = getBreedRule(breedRules, eq.breed);
          const recommendation = suggestNutritionByEquine(eq, activityInfo, breedRule);
          const liveWeight = num(latestWeightsByEquine.get(String(eq.id))?.weightKg || eq.weight, 0);
          return (
            <div key={eq.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16,display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"flex-start"}}>
                <div>
                  <div style={{fontWeight:800,color:T.text}}>{eq.name}</div>
                  <div style={{fontSize:12,color:T.muted}}>{eq.breed||"Equino"} · {liveWeight?`${round(liveWeight,1)} kg`:"peso não informado"}</div>
                </div>
                {canEdit && <Btn size="sm" variant="ghost" onClick={()=>setNutritionForm(plan?{...plan,_new:false}:{_new:true,equineId:eq.id,feedStockId:"",feedQty:"",feedPctBW:recommendation.suggestedPctBW,hayStockId:"",forageQty:"",foragePctBW:"1.50",hayQuality:"Boa",forageMealMode:"general",forageTimes:[...HARAS_HAY_TIMES],supplementStockId:"",supplementQty:"",specialDiet:false,specialDietReasons:[],mealMode:"general",feedTimes:[...defaultFeedTimes],notes:""})}>✏️ Editar</Btn>}
              </div>
              <div style={{display:'grid',gridTemplateColumns:isCompactViewport()?'1fr':'1.2fr 1fr',gap:12}}>
                <div style={{padding:12,borderRadius:12,background:T.card2,border:`1px solid ${T.border}`}}>
                  <div style={{fontSize:11,fontWeight:800,color:T.green,marginBottom:6}}>Plano alimentar</div>
                  <div style={{fontWeight:700,color:T.text}}>{feed?.name || 'Sem ração definida'}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:4}}>Ração: {plan?.feedQty || 0} kg/dia ({times.length||1}x) · Feno: {plan?.forageQty || 0} kg/dia ({forageTimes.length||1}x)</div>
                  <div style={{fontSize:12,color:T.muted}}>Sugestão ração: {recommendation.suggestedPctBW.toFixed(2)}% PV · faixa {recommendation.minPctBW.toFixed(2)}%–{recommendation.maxPctBW.toFixed(2)}%</div>
                  <div style={{fontSize:12,color:T.muted}}>Horários da ração: {times.join(', ') || "não definidos"}</div>
                  {feed && <button type="button" onClick={()=>setGuaranteeModal(feed)} style={{marginTop:8,background:"none",border:"none",padding:0,color:T.accent,cursor:"pointer",fontSize:12,fontWeight:700}}>Ver níveis de garantia</button>}
                </div>
                <div style={{padding:12,borderRadius:12,background:T.card2,border:`1px solid ${T.border}`}}>
                  <div style={{fontSize:11,fontWeight:800,color:T.yellow,marginBottom:6}}>Atividade e suplemento</div>
                  <div style={{fontWeight:700,color:T.text}}>{activityInfo.levelName}</div>
                  <div style={{fontSize:12,color:T.muted,marginTop:4}}>{activityInfo.totalSessions} sessão(ões)/semana</div>
                  <div style={{fontSize:12,color:T.muted}}>{supp?.name || 'Sem suplementação'} · {plan?.supplementQty || 0} kg/dia</div>
                  <div style={{fontSize:12,color:T.muted}}>{hay?.name || 'Sem feno definido'} · qualidade {plan?.hayQuality || 'Boa'}</div>
                  <div style={{fontSize:12,color:T.muted}}>Horários do feno: {forageTimes.join(', ') || "não definidos"}</div>
                  {!!plan?.specialDiet && <div style={{fontSize:12,color:T.red,fontWeight:700,marginTop:6}}>Dieta especial</div>}
                </div>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{times.map(hour=><span key={hour} style={{padding:"4px 8px",borderRadius:999,background:`${T.accent}18`,color:T.accent,fontSize:11,fontWeight:700}}>{hour}</span>)}</div>
              {!!plan?.notes && <div style={{fontSize:12,color:T.muted,borderTop:`1px solid ${T.border}`,paddingTop:8}}>{plan.notes}</div>}
            </div>
          );
        })}
      </div>
      {(rationItems.length===0 && supplementItems.length===0) && <div style={{marginTop:14,fontSize:12,color:T.yellow}}>⚠️ Cadastre produtos no Estoque com categoria <strong>Ração</strong> ou <strong>Suplementação</strong> para usar nesta aba.</div>}
    </div>
  );
}

function HarasFeedScheduleTab({ equines, nutritionByEquine, canEdit, defaultFeedTimes, setFeedTimesForm }) {
  const sortedEquines = React.useMemo(()=> (equines||[]).slice().sort((a,b)=>String(a?.name||"").localeCompare(String(b?.name||""),"pt-BR")), [equines]);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16}}>
        <div style={{fontWeight:800,color:T.text,marginBottom:6}}>Horários gerais do Haras</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>{defaultFeedTimes.map(hour=><span key={hour} style={{padding:"6px 10px",borderRadius:999,background:`${CC_COLORS["Haras"]}18`,color:CC_COLORS["Haras"],fontSize:12,fontWeight:700}}>{hour}</span>)}</div>
        <div style={{fontSize:11,color:T.muted,marginTop:8}}>Edite a lista global em <strong>Admin &gt; Listas &gt; Horários de Alimentação do Haras</strong>.</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
        {sortedEquines.map(eq=>{
          const plan = nutritionByEquine.get(String(eq.id));
          const times = (plan?.feedTimes||[]).length ? plan.feedTimes : defaultFeedTimes;
          return <div key={eq.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14,display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center"}}><div><div style={{fontWeight:700,color:T.text}}>{eq.name}</div><div style={{fontSize:12,color:T.muted}}>{plan?.mealMode==="individual"?"Horários individuais":"Usa horários gerais"}</div></div>{canEdit&&<Btn size="sm" variant="ghost" onClick={()=>setFeedTimesForm({equineId:eq.id,mealMode:plan?.mealMode||"general",feedTimes:(plan?.feedTimes||[]).length?[...plan.feedTimes]:[...defaultFeedTimes]})}>✏️ Ajustar</Btn>}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{times.map(hour=><span key={hour} style={{padding:"4px 8px",borderRadius:999,border:`1px solid ${T.border}`,background:T.card2,fontSize:11,color:T.text}}>{hour}</span>)}</div>
          </div>;
        })}
      </div>
    </div>
  );
}

function HarasManagementTab({ managementServices, equines, canEdit, setManagementForm, setServices, syncOnSave, show }) {
  return (
    <div>
      {canEdit&&<div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}><Btn onClick={()=>setManagementForm({_new:true,equineId:"",managementType:"",plannedDate:new Date().toISOString().slice(0,10),plannedTimes:[],notes:"",status:"Aberto"})}>+ Novo Manejo</Btn></div>}
      {managementServices.length===0&&<div style={{color:T.muted,textAlign:"center",padding:48}}>Nenhum manejo agendado ainda.</div>}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>
        {managementServices.map(item=>{
          const eq = equines.find(e=>String(e.id)===String(item.equineId));
          return <div key={item.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:16,display:"flex",flexDirection:"column",gap:10}}>
            <div style={{display:"flex",justifyContent:"space-between",gap:10}}><div><div style={{fontWeight:800,color:T.text}}>{item.harasManagementType||item.title}</div><div style={{fontSize:12,color:T.muted}}>{eq?.name||item.equineName||"Equino não informado"}</div></div><Badge color={item.status==="Concluído"?T.green:item.status==="Em Execução"?T.yellow:T.accent}>{item.status||"Aberto"}</Badge></div>
            <div style={{fontSize:12,color:T.text}}>📅 {item.plannedDate||item.date ? fmtDate(item.plannedDate||item.date||"") : "Sem data"}</div>
            {!!(item.plannedTimes||[]).length&&<div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{(item.plannedTimes||[]).map(hour=><span key={hour} style={{padding:"4px 8px",borderRadius:999,background:`${T.yellow}18`,color:T.yellow,fontSize:11,fontWeight:700}}>{hour}</span>)}</div>}
            {!!item.notes&&<div style={{fontSize:12,color:T.muted}}>{item.notes}</div>}
            {canEdit&&<div style={{display:"flex",gap:8,justifyContent:"flex-end"}}><Btn size="sm" variant="ghost" onClick={()=>setManagementForm({...item,_new:false,managementType:item.harasManagementType||item.title,plannedDate:item.plannedDate||item.date||"",plannedTimes:[...(item.plannedTimes||[])]})}>✏️ Editar</Btn><Btn size="sm" variant="danger" onClick={()=>{setServices(prev=>dedupeById((prev||[]).filter(s=>String(s.id)!==String(item.id)))); syncOnSave("services"); show("🗑️ Manejo removido.");}}>Excluir</Btn></div>}
          </div>;
        })}
      </div>
      <div style={{fontSize:11,color:T.muted,marginTop:12}}>Cada manejo criado aqui já nasce como <strong>Serviço interno</strong> vinculado ao Haras.</div>
    </div>
  );
}

// ── Haras Schedule Tab ────────────────────────────────────────────────────────
function HarasScheduleTab({ schedule, equines, canEdit, schedForm, setSchedForm,
  saveScheduleItem, setSchedule, syncOnSave, show,
  myName, isCollab, managementServices=[] }) {
  const SL = useSysLists();

  const [selectedEquine, setSelectedEquine] = useState("all");
  const [anchorDate,     setAnchorDate]     = useState(new Date());
  const [skipModal,      setSkipModal]      = useState(null);
  const [selectedSkip,   setSelectedSkip]   = useState(null);
  const [customSkip,     setCustomSkip]     = useState("");
  const [viewMode,       setViewMode]       = useState("month");

  const sortedEquines = React.useMemo(()=>sortAlpha(equines, item => item?.name || item?.registration || ""), [equines]);
  const visibleEquines = React.useMemo(()=>sortedEquines.filter(e=>e.status==="Ativo"), [sortedEquines]);
  const selectedDay = anchorDate.getDay();
  const defSched = {_new:true, equineId:"", day:selectedDay, hour:"08:00", type:TRAINING_TYPES[0], duration:60, notes:""};
  const renderScheduleEditor = () => schedForm ? <div style={{background:T.card,border:`1px solid ${CC_COLORS["Haras"]}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>{schedForm._new?"Agendar Treino":"Editar Treino"}</div><Btn variant="ghost" onClick={()=>setSchedForm(null)}>← Fechar</Btn></div><div style={{display:"flex",flexDirection:"column",gap:14}}><Field label="Equino *"><Select value={schedForm.equineId} onChange={e=>setSchedForm(f=>({...f,equineId:e.target.value}))}><option value="">Selecione...</option>{visibleEquines.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}</Select></Field><div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"1fr 1fr",gap:12}}><Field label="Dia da Semana"><Select value={schedForm.day} onChange={e=>setSchedForm(f=>({...f,day:Number(e.target.value)}))}>{WEEK_DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}</Select></Field><Field label="Horário"><Input type="time" value={schedForm.hour} onChange={e=>setSchedForm(f=>({...f,hour:e.target.value}))}/></Field><Field label="Tipo de Atividade"><Select value={schedForm.type} onChange={e=>setSchedForm(f=>({...f,type:e.target.value}))}>{(SL.TRAINING_TYPES||TRAINING_TYPES).map(t=><option key={t}>{t}</option>)}</Select></Field><Field label="Duração (min)"><Input type="number" min="15" step="15" value={schedForm.duration||60} onChange={e=>setSchedForm(f=>({...f,duration:Number(e.target.value)}))}/></Field></div><Field label="Observações"><Input value={schedForm.notes||""} placeholder="Instruções específicas..." onChange={e=>setSchedForm(f=>({...f,notes:e.target.value}))}/></Field><div style={{display:"flex",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}><div>{!schedForm._new&&<Btn variant="danger" onClick={()=>{setSchedule(p=>dedupeById((p||[]).filter(s=>s.id!==schedForm.id)));setSchedForm(null);show("🗑️ Removido.");syncOnSave("harasSchedule");}}>🗑️ Excluir</Btn>}</div><div style={{display:"flex",gap:10}}><Btn variant="ghost" onClick={()=>setSchedForm(null)}>Cancelar</Btn><Btn onClick={saveScheduleItem}>Salvar</Btn></div></div></div></div> : null;
  const renderSkipEditor = () => skipModal ? <div style={{background:T.card,border:`1px solid ${T.yellow}`,borderRadius:18,padding:16,boxShadow:"0 10px 26px rgba(15,23,42,.08)"}}><div style={{display:"flex",justifyContent:"space-between",gap:10,alignItems:"center",marginBottom:10,flexWrap:"wrap"}}><div style={{fontWeight:800,color:T.text,fontSize:18}}>⚠️ Justificar Não Realização</div><Btn variant="ghost" onClick={()=>{setSkipModal(null);setSelectedSkip(null);setCustomSkip("");}}>← Fechar</Btn></div><div style={{display:"flex",flexDirection:"column",gap:12}}><div style={{background:`${T.yellow}12`,border:`1px solid ${T.yellow}33`,borderRadius:10,padding:"10px 14px"}}><div style={{fontWeight:700,color:T.text}}>{skipModal.type}</div><div style={{fontSize:12,color:T.muted}}>{WEEK_DAYS[skipModal.day]} · {skipModal.hour} · {skipModal.duration}min</div></div><div style={{display:"flex",flexDirection:"column",gap:5,maxHeight:280,overflowY:"auto"}}>{(SL.HARAS_TRAINING_REASONS||DEFAULT_SKIP_REASONS).map(r=><label key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,cursor:"pointer",border:`1px solid ${selectedSkip?.id===r.id?T.yellow:T.border}`,background:selectedSkip?.id===r.id?`${T.yellow}12`:T.card2}}><input type="radio" checked={selectedSkip?.id===r.id} onChange={()=>setSelectedSkip(r)} style={{accentColor:T.yellow}}/><span style={{fontSize:18}}>{r.icon}</span><span style={{fontSize:13,fontWeight:selectedSkip?.id===r.id?700:400,color:selectedSkip?.id===r.id?T.text:T.muted}}>{r.label}</span></label>)}</div>{selectedSkip?.id===12&&<Input placeholder="Descreva o motivo..." value={customSkip} onChange={e=>setCustomSkip(e.target.value)}/>}<div style={{display:"flex",gap:10,justifyContent:"flex-end",flexWrap:"wrap"}}><Btn variant="ghost" onClick={()=>{setSkipModal(null);setSelectedSkip(null);setCustomSkip("");}}>Cancelar</Btn><Btn onClick={()=>submitSkip(skipModal)} style={{background:T.yellow,color:"#fff",border:"none"}}>📋 Registrar</Btn></div></div></div> : null;

  function submitSkip(slot) {
    if(!selectedSkip){show("Selecione uma justificativa.","error");return;}
    const label = selectedSkip.id===12 ? customSkip : selectedSkip.label;
    if(!label.trim()){show("Descreva o motivo.","error");return;}
    const updated = {...slot,
      skips:[...(slot.skips||[]),{
        ts:new Date().toISOString(), by:myName,
        reason:`${selectedSkip.icon} ${label}`,
        weekDate: anchorDate.toLocaleDateString("pt-BR"),
      }]
    };
    setSchedule(p=>p.map(s=>s.id===slot.id?updated:s));
    syncOnSave("harasSchedule");
    setSkipModal(null); setSelectedSkip(null); setCustomSkip("");
    show("📋 Justificativa registrada!");
  }

  const getSlots = React.useCallback((equineId, day) =>
    schedule.filter(s=>s.equineId===equineId&&s.day===day)
            .sort((a,b)=>String(a.hour||"").localeCompare(String(b.hour||""))),
  [schedule]);

  const displayEquines = selectedEquine==="all"
    ? visibleEquines
    : visibleEquines.filter(e=>e.id===selectedEquine);
  const scheduleFocusedEquineId = String((schedForm && !schedForm._new ? schedForm.equineId : "") || skipModal?.equineId || "");
  const agendaVisibleEquines = React.useMemo(()=> (schedForm || skipModal)
    ? (schedForm?._new ? [] : (scheduleFocusedEquineId ? displayEquines.filter(e=>String(e.id)===scheduleFocusedEquineId) : []))
    : displayEquines,
  [schedForm, skipModal, scheduleFocusedEquineId, displayEquines]);

  const calendarMonthLabel = anchorDate.toLocaleDateString("pt-BR", { month:"long", year:"numeric" });
  const currentDateLabel = anchorDate.toLocaleDateString("pt-BR", { weekday:"long", day:"2-digit", month:"2-digit" });
  const weekStart = React.useMemo(()=>{
    const d = new Date(anchorDate);
    const day = d.getDay();
    const diff = day===0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0,0,0,0);
    return d;
  }, [anchorDate]);
  const weekDays = React.useMemo(()=>Array.from({length:7}, (_, idx)=>{
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate()+idx);
    return d;
  }), [weekStart]);

  const monthCells = React.useMemo(()=>{
    const first = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
    const start = new Date(first);
    const firstDay = first.getDay();
    const diff = firstDay===0 ? -6 : 1 - firstDay;
    start.setDate(first.getDate() + diff);
    start.setHours(0,0,0,0);
    return Array.from({length:42}, (_, idx)=>{
      const d = new Date(start);
      d.setDate(start.getDate()+idx);
      return d;
    });
  }, [anchorDate]);

  const trainingDots = React.useMemo(()=>{
    const map = new Map();
    monthCells.forEach(date=>{
      const weekday = date.getDay();
      const count = agendaVisibleEquines.reduce((sum, eq)=> sum + getSlots(eq.id, weekday).length, 0);
      if(count>0) map.set(date.toISOString().slice(0,10), { trainings: count, managements: 0 });
    });
    (managementServices||[]).forEach(item=>{
      const key = String(item?.plannedDate || item?.date || "").slice(0,10);
      if(!key) return;
      const cur = map.get(key) || { trainings: 0, managements: 0 };
      cur.managements += 1;
      map.set(key, cur);
    });
    return map;
  }, [monthCells, agendaVisibleEquines, getSlots, managementServices]);

  const changeView = (mode)=>{
    setViewMode(mode);
  };
  const shiftAnchor = (step)=>{
    const next = new Date(anchorDate);
    if(viewMode==="month") next.setMonth(next.getMonth()+step);
    else if(viewMode==="week") next.setDate(next.getDate() + (7*step));
    else next.setDate(next.getDate()+step);
    setAnchorDate(next);
  };

  const monthSummaryText = `Treinos recorrentes e manejos do mês de ${calendarMonthLabel}`;

  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"1fr":"minmax(180px,220px) auto minmax(220px,1fr) auto",gap:10,marginBottom:16,alignItems:"center"}}>
        <Select value={selectedEquine} onChange={e=>setSelectedEquine(e.target.value)} style={{flex:1,maxWidth:220}}>
          <option value="all">Todos os equinos</option>
          {visibleEquines.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
        </Select>
        <div style={{display:"flex",gap:4,background:T.card2,borderRadius:8,padding:2,flexWrap:"wrap"}}>
          {[{id:"month",label:"🗓️ Mês"},{id:"week",label:"📅 Semana"},{id:"day",label:"📋 Dia"}].map(m=>(
            <div key={m.id} onClick={()=>changeView(m.id)}
              style={{padding:"6px 12px",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600,
                background:viewMode===m.id?T.accent:"transparent",
                color:viewMode===m.id?"#fff":T.muted,transition:"all .15s"}}>
              {m.label}
            </div>
          ))}
        </div>
        <div style={{display:"flex",alignItems:"center",justifyContent:isCompactViewport()?"space-between":"center",gap:8,flexWrap:"wrap"}}>
          <Btn variant="ghost" onClick={()=>shiftAnchor(-1)}>←</Btn>
          <div style={{fontSize:13,fontWeight:700,color:T.text,textTransform:viewMode==="month"?"capitalize":"none"}}>
            {viewMode==="month" ? calendarMonthLabel : viewMode==="week" ? `${weekDays[0].toLocaleDateString("pt-BR")} - ${weekDays[6].toLocaleDateString("pt-BR")}` : currentDateLabel}
          </div>
          <Btn variant="ghost" onClick={()=>shiftAnchor(1)}>→</Btn>
        </div>
        {canEdit&&<Btn onClick={()=>setSchedForm({...defSched, day:selectedDay})}>+ Agendar</Btn>}
      </div>

      {schedForm?._new && <div style={{marginBottom:16}}>{renderScheduleEditor()}</div>}

      {viewMode==="day"&&(
        <div style={{display:"grid",gridTemplateColumns:isCompactViewport()?"repeat(4,minmax(0,1fr))":"repeat(7,minmax(36px,1fr))",gap:4,marginBottom:16}}>
          {WEEK_DAYS_MONDAY.map(({label,index})=>{
            const active = selectedDay===index;
            return <div key={index} onClick={()=>{
              const next = new Date(anchorDate);
              const day = next.getDay();
              const diff = index - day;
              next.setDate(next.getDate()+diff);
              setAnchorDate(next);
            }}
              style={{height:36,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:11,fontWeight:700,
                background:active?CC_COLORS["Haras"]:T.card2,color:active?"#fff":T.muted,border:`1px solid ${active?CC_COLORS["Haras"]:T.border}`}}>{label}</div>;
          })}
        </div>
      )}

      {visibleEquines.length===0&&(
        <div style={{color:T.muted,textAlign:"center",padding:48}}>Cadastre equinos no Plantel primeiro.</div>
      )}

      {viewMode==="month"&&(
        <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:14,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,gap:8,flexWrap:"wrap"}}>
            <div style={{fontWeight:700,color:T.text}}>Calendário mensal</div>
            <div style={{fontSize:12,color:T.muted}}>{monthSummaryText}</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:6,marginBottom:8}}>
            {WEEK_DAYS_MONDAY.map(({label})=><div key={label} style={{textAlign:"center",fontSize:11,fontWeight:700,color:T.muted,padding:"4px 0"}}>{label}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,minmax(0,1fr))",gap:6}}>
            {monthCells.map(date=>{
              const key = date.toISOString().slice(0,10);
              const events = trainingDots.get(key);
              const inMonth = date.getMonth()===anchorDate.getMonth();
              const isToday = key===new Date().toISOString().slice(0,10);
              return (
                <button type="button" key={key} onClick={()=>{setAnchorDate(new Date(date)); setViewMode("day");}}
                  style={{minHeight:isCompactViewport()?58:72,borderRadius:12,border:`1px solid ${isToday?CC_COLORS["Haras"]:T.border}`,
                    background:inMonth?T.card2:`${T.card2}88`,padding:8,textAlign:"left",cursor:"pointer",position:"relative"}}>
                  <div style={{fontSize:12,fontWeight:700,color:inMonth?T.text:T.muted}}>{date.getDate()}</div>
                  {events&&(
                    <div style={{position:"absolute",right:8,bottom:8,display:"flex",gap:6,alignItems:"center"}}>
                      {events.trainings>0&&<span title={`${events.trainings} treino(s)`} style={{width:12,height:12,borderRadius:"50%",background:`${CC_COLORS["Haras"]}88`,display:"inline-block"}} />}
                      {events.managements>0&&<span title={`${events.managements} manejo(s)`} style={{width:12,height:12,borderRadius:"50%",background:`${T.yellow}88`,display:"inline-block"}} />}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:12,fontSize:12,color:T.muted}}>
            <span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:10,height:10,borderRadius:"50%",background:`${CC_COLORS["Haras"]}88`,display:"inline-block"}} /> Treino</span>
            <span style={{display:"inline-flex",alignItems:"center",gap:6}}><span style={{width:10,height:10,borderRadius:"50%",background:`${T.yellow}88`,display:"inline-block"}} /> Manejo</span>
          </div>
        </div>
      )}

      {viewMode==="day"&&(
        <div>
          <div style={{fontWeight:700,color:T.text,fontSize:16,marginBottom:14}}>
            {anchorDate.toLocaleDateString("pt-BR", { weekday:"long", day:"2-digit", month:"2-digit" })} — Agenda do dia
          </div>
          {agendaVisibleEquines.map(eq=>{
            const slots = getSlots(eq.id, selectedDay);
            if(slots.length===0) return null;
            return(
              <div key={eq.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,marginBottom:12,overflow:"hidden"}}>
                <div style={{background:`${CC_COLORS["Haras"]}18`,borderBottom:`1px solid ${CC_COLORS["Haras"]}22`,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
                  {eq.photo?<img src={eq.photo} alt="" style={{width:32,height:32,borderRadius:6,objectFit:"cover"}}/>:<HorseIcon size={28} />}
                  <div style={{fontWeight:700,color:T.text,fontSize:14}}>{eq.name}</div>
                  <div style={{fontSize:12,color:CC_COLORS["Haras"],marginLeft:4}}>{eq.breed}</div>
                </div>
                {slots.map(slot=>{
                  const daySkips=(slot.skips||[]).filter(sk=>sk.weekDate===anchorDate.toLocaleDateString("pt-BR"));
                  const skippedToday=daySkips.length>0;
                  const isSkippingSlot = !!(skipModal && String(skipModal.id)===String(slot.id));
                  const isEditingSlot = !!(schedForm && !schedForm._new && String(schedForm.id)===String(slot.id));
                  return(
                    <React.Fragment key={slot.id}>
                      <div style={{padding:"12px 16px",borderBottom:`1px solid ${T.border}22`,background:skippedToday?`${T.yellow}08`:T.card}}>
                        <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                          <div style={{width:52,height:52,borderRadius:10,background:`${CC_COLORS["Haras"]}22`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                            <div style={{fontWeight:800,color:CC_COLORS["Haras"],fontSize:13}}>{slot.hour}</div>
                          </div>
                          <div style={{flex:1}}>
                            <div style={{fontWeight:700,color:T.text,fontSize:14}}>{slot.type}</div>
                            {slot.duration&&<div style={{fontSize:12,color:T.muted}}>{slot.duration} min</div>}
                            {slot.notes&&<div style={{fontSize:11,color:T.muted,marginTop:2}}>{slot.notes}</div>}
                          </div>
                          {skippedToday
                            ? <div style={{background:`${T.yellow}18`,border:`1px solid ${T.yellow}44`,borderRadius:8,padding:"6px 10px",fontSize:11}}>
                                <div style={{color:T.yellow,fontWeight:700}}>⚠️ Justificado neste dia</div>
                                <div style={{color:T.muted,marginTop:2}}>{daySkips[daySkips.length-1].reason}</div>
                              </div>
                            : <Btn size="sm" variant="ghost" style={{borderColor:T.red,color:T.red}} onClick={()=>{setSkipModal(slot); setSelectedSkip(null); setCustomSkip("");}}>⚠️ Não realizado</Btn>
                          }
                          {canEdit&&<Btn size="sm" variant="ghost" onClick={()=>setSchedForm({...slot,_new:false})}>✏️</Btn>}
                        </div>
                      </div>
                      {isSkippingSlot && <div style={{padding:"0 16px 16px",background:T.card}}>{renderSkipEditor()}</div>}
                      {isEditingSlot && <div style={{padding:"0 16px 16px",background:T.card}}>{renderScheduleEditor()}</div>}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
          {agendaVisibleEquines.every(eq=>getSlots(eq.id,selectedDay).length===0)&&(
            <div style={{color:T.muted,textAlign:"center",padding:48}}>Nenhuma atividade programada para este dia.</div>
          )}
        </div>
      )}

      {viewMode==="week"&&agendaVisibleEquines.map(eq=>(
        <div key={eq.id} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,overflow:"hidden",marginBottom:16}}>
          <div style={{background:`${CC_COLORS["Haras"]}18`,borderBottom:`1px solid ${CC_COLORS["Haras"]}33`,padding:"10px 16px",display:"flex",alignItems:"center",gap:12}}>
            {eq.photo?<img src={eq.photo} alt="" style={{width:36,height:36,borderRadius:8,objectFit:"cover"}}/>:<HorseIcon size={32} />}
            <div style={{flex:1}}>
              <div style={{fontWeight:700,color:T.text,fontSize:15}}>{eq.name}</div>
              <div style={{fontSize:12,color:CC_COLORS["Haras"]}}>{eq.breed}</div>
            </div>
            <div style={{fontSize:12,color:T.muted}}>{schedule.filter(s=>s.equineId===eq.id).length} sessão(ões)/semana</div>
          </div>
          <div style={{overflowX:"auto"}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",minWidth:700}}>
              {weekDays.map((date, orderIdx)=>{
                const index = date.getDay();
                const label = date.toLocaleDateString("pt-BR", { weekday:"short" });
                const slots=getSlots(eq.id,index);
                const isToday=date.toISOString().slice(0,10)===new Date().toISOString().slice(0,10);
                return(
                  <div key={date.toISOString()} style={{borderRight:orderIdx<6?`1px solid ${T.border}22`:"none",minHeight:110,background:isToday?`${CC_COLORS["Haras"]}08`:"transparent"}}
                    onClick={()=>{setAnchorDate(new Date(date)); setViewMode("day");}}>
                    <div style={{padding:"6px 8px",background:isToday?`${CC_COLORS["Haras"]}22`:T.card2,textAlign:"center",borderBottom:`1px solid ${T.border}22`,cursor:"pointer"}}>
                      <div style={{fontSize:11,fontWeight:700,color:isToday?CC_COLORS["Haras"]:T.text,textTransform:"capitalize"}}>{label}</div>
                      <div style={{fontSize:10,color:isToday?CC_COLORS["Haras"]:T.muted}}>{date.toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit" })}</div>
                    </div>
                    <div style={{padding:4,display:"flex",flexDirection:"column",gap:3}}>
                      {slots.map(slot=><div key={slot.id} style={{background:`${CC_COLORS["Haras"]}18`,borderRadius:5,padding:"3px 5px",fontSize:10}}><div style={{fontWeight:700,color:CC_COLORS["Haras"]}}>{slot.hour}</div><div style={{color:T.text}}>{slot.type}</div></div>)}
                      {slots.length===0&&<div style={{fontSize:9,color:T.muted,textAlign:"center",padding:"6px 0"}}>—</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}

    </div>
  );
}



  return HarasModule;
}
